/**
 * French UI copy: the source language (redesign, D-145). Wording comes from the
 * redesign mockups and GLOSSARY-FR.md; `en.ts` mirrors every key.
 *
 * Rules a test enforces (test/i18n.test.ts): glossary terms only
 * (never "Flow", "QUICK", "runs", "Inbox"), and every key named `placeholder`
 * or ending in `Placeholder` starts with "ex. " so it can never pass for a value.
 * User data (automation names, tags, captions) never goes through these files.
 */
export const fr = {
  common: {
    optional: 'facultatif',
    loading: 'Chargement…',
    close: 'Fermer',
    dismiss: 'Masquer',
    back: 'Retour',
    removeTag: 'Retirer le tag {{name}}',
    closeDialog: 'Fermer la fenêtre',
    loadingList: 'Chargement',
    dismissNotification: 'Masquer la notification',
    uploadTooLarge: 'Ce fichier est trop volumineux pour être envoyé.',
    uploadFailed: 'L’envoi a échoué.',
    uploadOffline: 'L’envoi a échoué. Vérifiez votre connexion et réessayez.',
  },
  errorPage: {
    title: 'Un problème est survenu sur cette page',
    body: 'Rien de ce que vous avez enregistré n’est perdu. Recharger la page suffit en général ; si le problème revient, dites-nous ce que vous faisiez.',
    reload: 'Recharger la page',
  },
  /** StatusBadge labels: GLOSSARY-FR §2. */
  status: {
    draft: 'Brouillon',
    active: 'Active',
    paused: 'En pause',
    scheduled: 'Programmée',
    published: 'Publiée',
    failed: 'Échec',
    cancelled: 'Annulée',
    stopped: 'Arrêtée',
    completed: 'Terminée',
    noTime: 'Sans horaire',
    unpublishedChanges: 'Modifications non publiées',
    atLimit: 'Limite atteinte',
    connected: 'Connecté',
    expired: 'Expiré',
    error: 'Erreur',
    disconnected: 'Déconnecté',
  },
  channel: {
    instagram: 'Instagram',
    messenger: 'Messenger',
    facebook: 'Facebook',
  },
  /** « Enregistré · à l’instant » à côté d’un formulaire enregistré automatiquement. */
  saveState: {
    saved: 'Enregistré',
    saving: 'Enregistrement…',
    savingAnnounce: 'Enregistrement',
    failed: 'Échec de l’enregistrement — {{reason}}',
    failedAnnounce: 'Échec de l’enregistrement : {{reason}}',
    connectionDropped: 'La connexion a été interrompue.',
    retry: 'Réessayer',
  },
  /** Posts and Stories lists (R12–R15, R20–R23); the rest of these screens migrates in Phase 5. */
  posts: {
    /** What each list says about itself (R12–R15, R20–R23): `post` or `story`. */
    kind: {
      post: {
        title: 'Publications',
        lede: 'Programmez photos, carrousels et reels sur Instagram et Facebook.',
        newLabel: 'Nouvelle publication',
        tabsLabel: 'Statut des publications',
        tableLabel: 'Publications',
        noun: 'Publication',
        cancelTitle: 'Annuler cette publication ?',
        cancelAction: 'Annuler la publication',
        deleteTitle: 'Supprimer cette publication ?',
        deleteAction: 'Supprimer la publication',
        deletePublished: 'Elle n’est supprimée que de Repliq : la publication reste sur {{where}}.',
        canceled: 'Publication annulée',
        retried: 'Nouvel essai de publication lancé',
        deleted: 'Publication supprimée',
        insights: 'Statistiques de la publication',
        empty: {
          upcoming: {
            title: 'Rien de programmé',
            body: 'Les publications programmées apparaissent ici par date. Utilisez « Nouvelle publication » pour préparer la suivante.',
          },
          drafts: {
            title: 'Aucun brouillon',
            body: 'Commencez une publication et quittez la page : dès qu’elle a une photo ou une vidéo, elle est enregistrée ici automatiquement.',
          },
          published: {
            title: 'Rien de publié pour l’instant',
            body: 'Les publications parues affichent ici leurs vues, leur portée et les interactions.',
          },
        },
      },
      story: {
        title: 'Stories',
        lede: 'Programmez vos stories sur Instagram et Facebook : une photo ou une vidéo chacune, visible 24 h.',
        newLabel: 'Nouvelle story',
        tabsLabel: 'Statut des stories',
        tableLabel: 'Stories',
        noun: 'Story',
        cancelTitle: 'Annuler cette story ?',
        cancelAction: 'Annuler la story',
        deleteTitle: 'Supprimer cette story ?',
        deleteAction: 'Supprimer la story',
        deletePublished: 'Elle n’est supprimée que de Repliq : la story reste sur {{where}} jusqu’à la fin de sa journée.',
        canceled: 'Story annulée',
        retried: 'Nouvel essai de publication lancé',
        deleted: 'Story supprimée',
        insights: 'Statistiques de la story',
        empty: {
          upcoming: {
            title: 'Aucune story programmée',
            body: 'Les stories programmées apparaissent ici par date. Utilisez « Nouvelle story » pour préparer la suivante.',
          },
          drafts: {
            title: 'Aucun brouillon',
            body: 'Commencez une story et quittez la page : dès qu’elle a une photo ou une vidéo, elle est enregistrée ici automatiquement.',
          },
          published: {
            title: 'Rien de publié pour l’instant',
            body: 'Les stories parues affichent ici leurs vues, leur portée et leurs réponses, recueillies pendant leurs 24 heures.',
          },
        },
      },
    },
    columns: {
      status: 'Statut',
      goesLive: 'Mise en ligne',
      edited: 'Dernière modification',
      reason: 'Raison',
      actions: 'Actions',
      views: 'Vues',
      reach: 'Portée',
      likes: 'J’aime',
      comments: 'Commentaires',
      saved: 'Enregistr.',
      replies: 'Réponses',
      shares: 'Partages',
    },
    /** The footer under the published numbers (A10-04). */
    legend: {
      post: '« — » : pas de donnée pour cette publication · « 0 » : aucune interaction',
      story: '« — » : pas encore de statistiques · « 0 » : aucune interaction',
    },
    /** A narrow list's numbers, on a line of their own. */
    narrow: {
      views_one: '{{value}} vue',
      views_other: '{{value}} vues',
      likes_one: '{{value}} j’aime',
      likes_other: '{{value}} j’aime',
      replies_one: '{{value}} réponse',
      replies_other: '{{value}} réponses',
    },
    noCaption: 'Sans légende',
    storyFallback: 'Story',
    items_one: '{{count}} élément',
    items_other: '{{count}} éléments',
    rowActions: 'Actions pour « {{name}} »',
    publishing: 'Publication en cours…',
    alsoOn: 'Aussi sur {{name}}',
    campaignChip: 'Campagne · {{name}}',
    automationAnswers: '{{name}} répond aux commentaires de cette publication',
    automationOff: '{{name}} est désactivée',
    off: '(désactivée)',
    onPage: 'Sur {{page}} : {{error}}',
    retry: 'Réessayer',
    synced: 'Statistiques synchronisées {{ago}}',
    menu: {
      edit: 'Modifier',
      view: 'Ouvrir',
      cancel: 'Annuler',
      retry: 'Réessayer maintenant',
      insights: 'Statistiques',
      platformInsights: 'Statistiques {{platform}}',
      viewOn: 'Voir sur {{platform}}',
      delete: 'Supprimer',
    },
    /** The slide's words when the menu's are too long for it (D-196). */
    swipe: { retry: 'Réessayer' },
    keep: 'La garder',
    pausedAutomations_one: '{{count}} automatisation mise en pause : {{names}}.',
    pausedAutomations_other: '{{count}} automatisations mises en pause : {{names}}.',
    actionFailed: 'L’action n’a pas abouti',
    deleteUnpublished: 'Elle ne sera pas publiée. Les médias importés restent dans votre bibliothèque.',
    deleteCopy: 'Sa copie sur {{page}} est supprimée avec elle.',
    deleteAutomationsOne: 'L’automatisation {{names}} y est liée et sera mise en pause.',
    deleteAutomationsMany: 'Les {{count}} automatisations {{names}} y sont liées et seront mises en pause.',
    tabs: {
      upcoming: 'À venir',
      drafts: 'Brouillons',
      published: 'Publiées',
      failed: 'Échecs & annulées',
    },
    /** Screen-reader words for the red count: colour never speaks alone. */
    failedCount: '{{count}} en échec',
    cancelledNote: 'Annulée : elle ne sera pas publiée.',
    storyTitle: 'Story du {{date}}',
    plannedFor: 'prévue le {{when}}',
    edited: 'Dernière modification : {{when}}',
    noFailures: {
      post: {
        title: 'Aucun échec de publication.',
        body: 'Si Instagram ou Facebook refuse une publication, elle apparaît ici avec la raison et un bouton pour réessayer.',
      },
      story: {
        title: 'Aucune story en échec',
        body: 'Si Instagram ou Facebook refuse une story, elle arrive ici avec la raison et un bouton pour réessayer.',
      },
    },
    cancelConfirm: {
      intro: {
        post: 'Elle ne sera pas publiée.',
        story: 'Elle ne sera pas publiée.',
      },
      waitsOne: {
        post: 'L’automatisation {{names}} l’attend et reste configurée : elle s’activera si vous programmez de nouveau la publication.',
        story: 'L’automatisation {{names}} l’attend et reste configurée : elle s’activera si vous programmez de nouveau la story.',
      },
      waitsMany: {
        post: 'Les {{count}} automatisations {{names}} l’attendent et restent configurées : elles s’activeront si vous programmez de nouveau la publication.',
        story: 'Les {{count}} automatisations {{names}} l’attendent et restent configurées : elles s’activeront si vous programmez de nouveau la story.',
      },
    },
  },
  /**
   * The publications calendar (Murmura M4, D-188): `CalendarMonth.dc.html` and
   * `P7-Calendar-Empty.dc.html`. The stats, chips and legend reuse `posts.*`,
   * `status.*` and `channel.*`.
   */
  calendar: {
    /** The header when the calendar shows one campaign (`?campaign=`, D-190). */
    campaignPage: {
      eyebrow: 'Calendrier de la campagne',
      back: 'Retour à la campagne',
      lede: 'Les publications et stories de cette campagne. Effacez le filtre de campagne pour voir tout le calendrier.',
    },
    viewLabel: 'Affichage',
    views: {
      list: 'Liste',
      calendar: 'Calendrier',
    },
    /** The link of Stories and of a campaign's page to the calendar (PROPOSALS M4, Q25). */
    viewIn: 'Voir dans le calendrier',
    lede: 'Publications et stories Instagram et Facebook, au même endroit.',
    /** "1 publication, 1 story": the "À venir" stat and the "+n" button. */
    posts_one: '{{count}} publication',
    posts_other: '{{count}} publications',
    stories_one: '{{count}} story',
    stories_other: '{{count}} stories',
    kinds: '{{posts}}, {{stories}}',
    previousMonth: 'Mois précédent',
    nextMonth: 'Mois suivant',
    today: 'Aujourd’hui',
    filters: 'Filtres',
    campaign: 'Campagne',
    allCampaigns: 'Toutes les campagnes',
    legend: 'Légende',
    /** A day of the grid, for screen readers: "mer. 7 octobre 2026, 2 éléments". */
    day: '{{date}}, {{items}}',
    /** "+2 publications", "+5 · 3 publications, 2 stories". */
    moreOneKind: '+{{kinds}}',
    moreTwoKinds: '+{{count}} · {{kinds}}',
    /** An item's full name: "Publication programmée, lun. 15 mars 2027 à 10:00, Instagram et Page ChatFlow". */
    item: '{{what}}, {{date}} à {{time}}, {{where}}',
    what: {
      post: {
        scheduled: 'Publication programmée',
        published: 'Publication publiée',
        failed: 'Publication en échec',
        draft: 'Brouillon de publication',
      },
      story: {
        scheduled: 'Story programmée',
        published: 'Story publiée',
        failed: 'Story en échec',
        draft: 'Brouillon de story',
      },
    },
    page: 'Page {{name}}',
    emptyMonth: {
      title: 'Rien de programmé en {{month}}.',
      next: 'Prochaine mise en ligne : {{date}} · {{time}}.',
      untimed_one: '{{count}} élément attend un horaire.',
      untimed_other: '{{count}} éléments attendent un horaire.',
      goTo: 'Aller au {{date}}',
    },
    zone: 'Heures affichées dans le fuseau de l’espace : {{zone}}.',
    /** "Mois / Semaine" (`P5-Calendar-Week.dc.html` l.43). */
    period: 'Période',
    periods: {
      month: 'Mois',
      week: 'Semaine',
    },
    previousWeek: 'Semaine précédente',
    nextWeek: 'Semaine suivante',
    thisWeek: 'Cette semaine',
    /** The week's card: "Semaine du 15 au 21 mars 2027" (P5 l.49). */
    weekName: 'Semaine du {{first}} au {{last}}',
    weekZone: 'Heures affichées dans le fuseau de l’espace : {{zone}}. Cliquez sur un élément pour l’ouvrir.',
    /** The side panel (`CalendarMonth.dc.html` l.94-121). */
    upcoming: 'Prochaines mises en ligne',
    openDraft: 'Ouvrir le brouillon',
    untimedFooter: 'Ouvrez un élément pour lui donner une date. Une campagne ne se lance qu’une fois toutes ses publications datées.',
    /** After "Image · @compte": "modifiée il y a 20 min", "modifiée à 14:06", "modifiée le 12 oct.". */
    edited: { ago: 'modifiée {{when}}', today: 'modifiée à {{when}}', date: 'modifiée le {{when}}' },
    /** The phone agenda (`P6-Calendar-Phone.dc.html` l.25-66): the chip "Tout", the warning row, "Publiées récemment", the footer. */
    all: 'Tout',
    untimedCount_one: '{{count}} élément sans horaire',
    untimedCount_other: '{{count}} éléments sans horaire',
    /** "2 dans Random Campaign · 1 brouillon". */
    inCampaign: '{{count}} dans {{name}}',
    drafts_one: '{{count}} brouillon',
    drafts_other: '{{count}} brouillons',
    recent: 'Publiées récemment',
    /** The `published` badge of a day with several items. */
    publishedMany: 'Publiées',
    agendaZone: 'Heures du fuseau de l’espace : {{zone}}.',
  },
  /**
   * Phase 2b (A00-02): every disabled primary says why, and every example
   * placeholder starts with "ex. ". Reasons and placeholders come from the
   * mockups (R33, R34, R39, R43, R44, R30, Builder, Starters, Composer).
   */
  /** Navigation: Sidebar.dc.html and GLOSSARY-FR §1. */
  postType: {
    image: 'Image',
    carousel: 'Carrousel',
    reel: 'Reel',
    story: 'Story',
  },
  ago: {
    now: 'à l’instant',
    minutes: 'il y a {{count}} min',
    hours: 'il y a {{count}} h',
    yesterday: 'hier',
    days_one: 'il y a {{count}} jour',
    days_other: 'il y a {{count}} jours',
  },
  /** The numbers modal and the notices under a published post or story (D-115). */
  insights: {
    loading: 'Chargement des statistiques',
    pending: 'Les statistiques arrivent sous 48 heures.',
    campaignAcross_one: 'Sur {{count}} publication parue. La portée s’additionne par publication : une personne qui a vu deux publications compte deux fois.',
    campaignAcross_other: 'Sur {{count}} publications parues. La portée s’additionne par publication : une personne qui a vu deux publications compte deux fois.',
    reconnect: 'Reconnecter',
    avgWatchTime: 'Durée moyenne de visionnage',
    automationsTitle: 'Automatisations sur cette publication',
    commentsReceived: 'Commentaires reçus',
    triggered: 'Ont lancé une automatisation',
    messagesSent: 'Messages privés envoyés',
    contactsReached: 'Personnes atteintes',
    dayByDay: 'Jour par jour',
    day: 'Jour',
    syncedAgo: 'Synchronisées {{ago}}',
    notSynced: 'Pas encore synchronisées',
    close: 'Fermer',
    refresh: 'Actualiser',
    refreshed: 'Statistiques actualisées',
    partlyRefreshed: 'Statistiques en partie actualisées',
    refreshFailed: 'Actualisation impossible',
    seconds: '{{value}} s',
    /** The server's insight codes (Q8): the web says them in the reader's language. */
    errors: {
      INSIGHTS_SCOPE_MISSING: 'Reconnectez {{account}} dans Paramètres › Canaux pour voir vues, portée, enregistrements et partages.',
      ACCOUNT_NOT_CONNECTED: '{{account}} n’est pas connecté. Reconnectez-le dans Paramètres › Canaux.',
      INSIGHTS_UNAVAILABLE: 'Les statistiques ne sont pas disponibles pour ce canal sur ce serveur.',
      MEDIA_GONE: 'Cette publication n’est plus sur {{platform}}.',
      SYNC_FAILED: '{{platform}} n’a pas renvoyé les statistiques de cette publication. Nouvel essai plus tard.',
    },
  },
  nav: {
    dashboard: 'Tableau de bord',
    home: 'Accueil',
    inbox: 'Conversations',
    contacts: 'Contacts',
    automations: 'Automatisations',
    templates: 'Modèles',
    posts: 'Publications',
    stories: 'Stories',
    campaigns: 'Campagnes',
    analytics: 'Statistiques',
    channels: 'Canaux',
    settings: 'Paramètres',
    team: 'Équipe',
    inboxSettings: 'Conversations',
    tags: 'Tags',
    fields: 'Champs personnalisés',
    usage: 'Mon offre',
    developers: 'Développeurs',
    groupAudience: 'Audience',
    groupAutomate: 'Automatiser',
    groupPublish: 'Publier',
    groupInsights: 'Analyser',
    more: 'Plus',
    main: 'Navigation principale',
    settingsSections: 'Sections des paramètres',
    search: 'Rechercher',
    unread_one: 'non lu',
    unread_other: 'non lus',
    invoice: 'Facture',
    invoiceToPay: 'Facture à régler',
    moreWithInvoice: 'Plus, facture à régler',
  },
  shell: {
    workspaceButton: 'Espace de travail : {{name}}. Changer d’espace de travail',
    workspaceLoading: 'chargement',
    workspaces: 'Espaces de travail',
    role: { owner: 'Propriétaire', admin: 'Admin', agent: 'Agent' },
    switchFailed: 'Impossible de changer d’espace de travail. Vérifiez votre connexion et réessayez.',
    createWorkspace: 'Créer un espace de travail',
    accountMenu: 'Menu du compte',
    account: 'Compte',
    signOut: 'Se déconnecter',
    signingOut: 'Déconnexion…',
    signOutFailed: 'Impossible de se déconnecter. Vérifiez votre connexion et réessayez.',
  },
  /** Ctrl K search: R40-CommandPalette.dc.html. */
  palette: {
    title: 'Recherche',
    inputLabel: 'Rechercher des pages, automatisations et contacts',
    prompt: 'Rechercher des pages, automatisations, contacts…',
    results: 'Résultats',
    sectionPages: 'Pages',
    sectionAutomations: 'Automatisations',
    sectionContacts: 'Contacts',
    searchingContacts: 'Recherche des contacts…',
    noResults: 'Aucun résultat pour « {{query}} ».',
    resultCount_one: '{{count}} résultat',
    resultCount_other: '{{count}} résultats',
    enter: 'Entrée',
    esc: 'Échap',
    typeToSearch: 'Tapez pour chercher aussi dans les automatisations et les contacts',
    toClose: 'pour fermer',
  },
  /** Conversations (R02, R03): Inbox.dc.html and R02-Inbox-Empty.dc.html. */
  inbox: {
    title: 'Conversations',
    open_one: '{{count}} ouverte',
    open_other: '{{count}} ouvertes',
    unread_one: '{{count}} non lue',
    unread_other: '{{count}} non lues',
    filters: 'Filtres',
    filtersUnreadOnly: 'Filtres (non lues seulement)',
    unreadOnly: 'Non lues seulement',
    awaitingOnly: 'En attente de réponse',
    filtersAwaitingOnly: 'Filtres (en attente de réponse)',
    filtersBoth: 'Filtres (non lues, en attente de réponse)',
    awaiting: 'En attente de réponse · {{duration}}',
    searchLabel: 'Rechercher des conversations',
    searchPrompt: 'Nom, e-mail ou message',
    statusLabel: 'Statut',
    statusOpen: 'Ouvertes',
    statusClosed: 'Clôturées',
    statusAll: 'Toutes',
    assigneeLabel: 'Assignée à',
    assigneeAnyone: 'Tout le monde',
    assigneeMine: 'Assignées à moi',
    assigneeUnassigned: 'Non assignées',
    listLoading: 'Chargement des conversations',
    noMatch: 'Aucune conversation ne correspond à ces filtres.',
    noClosed: 'Aucune conversation clôturée.',
    noOpen: 'Aucune conversation ouverte. Vous êtes à jour.',
    noMessagesYet: 'Aucun message',
    handledBy: 'Dernière réponse auto : {{name}}',
    automationsPaused: 'Automatisations en pause',
    closed: 'Clôturée',
    pickTitle: 'Choisissez une conversation',
    pickBody: 'Lisez l’historique et répondez. Celles où une automatisation a répondu en dernier sont marquées',
    pickMarker: 'Dernière réponse auto',
    neverTitle: 'Aucune conversation pour l’instant',
    neverBody: 'Connectez Instagram ou Messenger : chaque message privé arrive ici, traité par vos automatisations ou votre équipe.',
    connectChannel: 'Connecter un canal',
    setUpReplies: 'Configurer des réponses automatiques',
    unknownContact: 'Contact inconnu',
    mockChannel: 'Canal de test',
  },
  thread: {
    label: 'Conversation avec {{name}}',
    messages: 'Messages',
    back: 'Retour aux conversations',
    loading: 'Chargement de la conversation',
    gone: 'Cette conversation n’existe plus.',
    assignLabel: 'Assignée à',
    unassigned: 'Non assignée',
    /** The phone's icon-only assign control says who has the conversation (A16-02). */
    assignedToName: 'Assignée à {{name}}',
    assignNobody: 'Non assignée : assigner la conversation',
    resolve: 'Clôturer',
    reopen: 'Rouvrir',
    resolveLabel: 'Clôturer la conversation',
    reopenLabel: 'Rouvrir la conversation',
    loadEarlier: 'Charger les messages précédents',
    loadingMessages: 'Chargement des messages',
    noMessages: 'Aucun message dans cette conversation pour l’instant.',
    noText: '(sans texte)',
    system: 'Système',
    automatedReply: 'Réponse automatique',
    queued: 'En attente d’envoi',
    failed: 'Échec de l’envoi',
    timesTwo: '{{first}} et {{last}}',
    timesRange: 'de {{first}} à {{last}}',
    today: 'Aujourd’hui',
    yesterday: 'Hier',
    resumed: 'Automatisations reprises',
    resumeFailed: 'Impossible de reprendre les automatisations',
    pauseFailed: 'Impossible de mettre en pause',
    replyFailed: 'Réponse non envoyée',
    assignFailed: 'Impossible d’assigner la conversation',
    resolvedToast: 'Conversation clôturée',
    reopenedToast: 'Conversation rouverte',
    updateFailed: 'Impossible de modifier la conversation',
  },
  replyBox: {
    reply: 'Répondre',
    replyPrompt: 'Répondre à {{name}}…',
    send: 'Envoyer',
    sending: 'Envoi…',
    hintPause: 'Votre réponse met les automatisations en pause {{duration}} pour ce contact.',
    hintPauseUnknown: 'Votre réponse met les automatisations en pause pour ce contact.',
    hintNoPause: 'Vos réponses ne mettent pas les automatisations en pause.',
    hintReopen: 'Répondre rouvre la conversation.',
    windowClosed: 'Réponse impossible : {{channel}} n’accepte une réponse que dans les 24 h qui suivent le dernier message du contact, reçu il y a {{since}}. Vous pourrez répondre dès son prochain message.',
    windowNever: 'Réponse impossible : {{channel}} n’accepte une réponse qu’après un message du contact. Vous pourrez répondre dès son premier message.',
    windowClosing: 'Encore {{left}} pour répondre : passé ce délai, {{channel}} n’accepte plus de réponse avant le prochain message du contact.',
  },
  /** The bar above the thread: who answers this contact, and how long a reply pauses automations (R03). */
  handover: {
    none: 'Aucune automatisation en cours pour ce contact.',
    running: 'En cours pour ce contact : {{names}}.',
    waitsUntil: '{{name}} (attend jusqu’à {{time}})',
    ifReply: 'Si vous répondez, elles se mettent en pause',
    ifReplyUnknown: 'Si vous répondez, elles se mettent en pause.',
    noPauseOnReply: 'Vos réponses ne les mettent pas en pause.',
    pause: 'Mettre en pause',
    pausedUntil: 'Automatisations en pause pour ce contact jusqu’à {{time}}.',
    pausedShort: 'En pause jusqu’à {{time}}',
    runningShort: 'En cours : {{names}}',
    noneShort: 'Aucune automatisation en cours',
    pausedNote: 'Les autres contacts ne sont pas concernés. Clôturer la conversation lève aussi la pause.',
    resume: 'Reprendre',
  },
  duration: {
    minutes: '{{count}} min',
    hours_one: '{{count}} h',
    hours_other: '{{count}} h',
    days_one: '{{count}} jour',
    days_other: '{{count}} jours',
    week: '1 semaine',
  },
  /** Time left before a planned instant (`formatCountdown`, Murmura M1): "dans 10 h 16 min". */
  countdown: {
    in: 'dans',
    text: 'dans {{span}}',
    hours: '{{count}} h',
    hoursMinutes: '{{hours}} h {{minutes}} min',
  },
  contactPanel: {
    label: 'Contact',
    openContact: 'Ouvrir la fiche',
    tags: 'Tags',
    details: 'Détails',
    addTag: 'Ajouter un tag',
    noTagsYet: 'Aucun tag pour l’instant : créez-en un dans les paramètres',
    allTagsOn: 'Ce contact a déjà tous les tags.',
    loadingTags: 'Chargement des tags…',
    addTagFailed: 'Impossible d’ajouter le tag',
    email: 'E-mail',
    phone: 'Téléphone',
    firstSeen: 'Premier contact',
    sourceChannel: 'Canal d’origine',
    source: 'Origine',
    sourceManual: 'Ajouté manuellement',
    sourceImport: 'Import CSV',
    sourceApi: 'Ajouté par l’API',
    sourceAutomation: 'Créé par une automatisation',
    yes: 'Oui',
    no: 'Non',
    nothingOnFile: 'Rien d’enregistré pour l’instant.',
  },
  workspace: {
    namePlaceholder: 'ex. Acme Marketing',
    nameNeeded: 'Saisissez un nom pour continuer.',
    steps: 'Votre démarrage',
    step: {
      name: '1 · Nommer l’espace',
      channel: '2 · Connecter un canal',
      automation: '3 · Première automatisation',
    },
    stepCurrent: 'étape en cours',
    title: 'Nommez votre espace de travail',
    lede: 'Un espace regroupe vos contacts, vos automatisations et vos canaux connectés. Vous pourrez en créer d’autres plus tard.',
    name: 'Nom de l’espace',
    nameHint: 'En général, le nom de votre entreprise ou de votre marque.',
    create: 'Créer l’espace',
    creating: 'Création…',
  },
  auth: {
    rail: {
      tagline: 'Des conversations qui travaillent pendant votre absence.',
      body: 'Créez vos automatisations une fois : elles tournent sur Instagram et Messenger, là où vos clients vous écrivent déjà.',
      footer: 'Automatisation marketing multicanal',
    },
    genericError: 'Une erreur est survenue. Réessayez.',
    email: 'E-mail',
    password: 'Mot de passe',
    passwordRule: '10 caractères minimum.',
    backToLogin: 'Retour à la connexion',
    captcha: {
      pending: 'Vérification de sécurité en cours…',
      required: 'Terminez la vérification de sécurité pour continuer.',
      failed: 'La vérification de sécurité a été refusée. Réessayez.',
      unavailable: 'La vérification de sécurité est indisponible pour le moment. Réessayez dans un instant.',
    },
    login: {
      title: 'Bon retour',
      lede: 'Connectez-vous à votre espace de travail.',
      forgot: 'Mot de passe oublié ?',
      submit: 'Se connecter',
      submitting: 'Connexion…',
      noAccount: 'Pas encore de compte ?',
      register: 'Créer un compte',
    },
    register: {
      title: 'Créer votre compte',
      lede: 'Votre première automatisation en quelques minutes.',
      firstName: 'Prénom',
      lastName: 'Nom',
      submit: 'Créer mon compte',
      submitting: 'Création du compte…',
      already: 'Déjà inscrit ?',
      login: 'Se connecter',
      sentTitle: 'Vérifiez vos e-mails',
      sentLede: 'Nous avons envoyé un lien de vérification à {{email}}.',
      sentBody: 'Ouvrez le lien pour activer votre compte. En développement local, le message vous attend dans Mailpit à l’adresse',
    },
    forgot: {
      title: 'Mot de passe oublié',
      lede: 'Nous vous envoyons un lien par e-mail.',
      submit: 'Envoyer le lien',
      submitting: 'Envoi…',
      sent: 'Si un compte existe pour {{email}}, un lien de réinitialisation est en route. Il expire dans une heure.',
    },
    reset: {
      title: 'Choisir un nouveau mot de passe',
      lede: 'Cela vous déconnecte de tous vos autres appareils.',
      newPassword: 'Nouveau mot de passe',
      submit: 'Mettre à jour le mot de passe',
      submitting: 'Mise à jour…',
      done: 'Mot de passe mis à jour.',
      login: 'Se connecter',
      missingTitle: 'Ce lien de réinitialisation est incomplet.',
      missingBody: 'Il manque le jeton de sécurité : demandez un nouveau lien.',
      requestNew: 'Demander un nouveau lien',
    },
    verify: {
      title: 'Vérifier votre e-mail',
      lede: 'Un clic et votre compte est actif.',
      submit: 'Vérifier mon e-mail',
      submitting: 'Vérification…',
      done: 'Votre adresse e-mail est vérifiée.',
      continue: 'Continuer vers Repliq',
      missingTitle: 'Ce lien de vérification est incomplet.',
      missingBody: 'Demandez un nouvel e-mail de vérification.',
      requestNew: 'Demander un nouvel e-mail',
      requesting: 'Envoi…',
      resent: 'Si un compte non vérifié existe pour {{email}}, un nouveau lien de vérification est en route.',
    },
    invite: {
      title: 'Rejoindre l’espace de travail',
      ledeSignIn: 'Connectez-vous avec l’adresse qui a reçu l’invitation pour l’accepter.',
      ledeChecking: 'Vérification de votre session…',
      ledeAccepting: 'Vous acceptez en tant que {{email}}.',
      signIn: 'Se connecter pour continuer',
      accept: 'Accepter l’invitation',
      accepting: 'Connexion à l’espace…',
      missing: 'Ce lien d’invitation est incomplet : demandez à la personne qui vous a invité de vous le renvoyer.',
      /** The INVITING workspace is full (D-183): its name and its owner's, never "votre offre". */
      full_one: '{{workspaceName}} a atteint sa limite de {{limit}} utilisateur. Demandez à {{ownerName}} de libérer une place ou de changer d’offre.',
      full_other: '{{workspaceName}} a atteint sa limite de {{limit}} utilisateurs. Demandez à {{ownerName}} de libérer une place ou de changer d’offre.',
      /** `ownerName` when the inviting workspace has no active owner. */
      theOwner: 'son propriétaire',
    },
  },
  campaigns: {
    namePlaceholder: 'ex. Lancement collection automne',
    nameNeeded: 'Donnez un nom pour créer la campagne.',
    launchNeedsPosts: 'Ajoutez au moins une publication pour lancer la campagne.',
    title: 'Campagnes',
    lede: 'Planifiez une série de publications, vérifiez-les ensemble, lancez-les en une fois.',
    new: 'Nouvelle campagne',
    empty: {
      title: 'Aucune campagne pour l’instant',
      body: 'Une campagne est une liste de publications avec leurs horaires prévus. Rien ne part tant que vous ne l’avez pas lancée.',
      action: 'Créer une campagne',
    },
    meta: {
      posts_one: '{{count}} publication',
      posts_other: '{{count}} publications',
      stories_one: '{{count}} story',
      stories_other: '{{count}} stories',
      accounts_one: '{{count}} compte',
      accounts_other: '{{count}} comptes',
      noDates: 'pas encore d’horaire',
      none: 'Aucune publication pour l’instant',
      draftNote: 'rien ne part tant que vous n’avez pas lancé la campagne',
    },
    row: {
      untimed_one: '{{count}} publication sans horaire avant le lancement',
      untimed_other: '{{count}} publications sans horaire avant le lancement',
      addPost: 'Ajouter une publication',
      addPostTo: 'Ajouter une publication à {{name}}',
      progressLabel: 'Publiées',
      progress: '{{published}}/{{total}} publiées',
      failed_one: '{{count}} en échec',
      failed_other: '{{count}} en échec',
      /** The row's `···` and the tray a finger slides out (D-191). */
      actions: 'Actions de {{name}}',
      swipe: { pause: 'Pause', resume: 'Reprendre', delete: 'Supprimer' },
    },
    create: {
      title: 'Nouvelle campagne',
      name: 'Nom',
      description: 'Description',
      descriptionNote: 'facultatif, pour votre équipe',
      cancel: 'Annuler',
      submit: 'Créer la campagne',
    },
    detail: {
      back: 'Campagnes',
      actions: 'Actions de la campagne',
      add: 'Ajouter',
      addMenu: 'Ajouter à la campagne',
      addPost: 'Publication',
      addStory: 'Story',
      launch: 'Lancer la campagne',
      resume: 'Reprendre la campagne',
      rename: 'Renommer',
      pause: 'Mettre en pause',
      cancelCampaign: 'Arrêter la campagne',
      delete: 'Supprimer la campagne',
      toast: { launch: 'Campagne lancée', resume: 'Campagne reprise', pause: 'Campagne en pause', cancel: 'Campagne arrêtée', deleted: 'Campagne supprimée' },
      check: 'À vérifier avant de lancer :',
      untimed_one: '{{count}} publication n’a pas encore d’horaire.',
      untimed_other: '{{count}} publications n’ont pas encore d’horaire.',
      /** Under the disabled Launch (A11-01). */
      launchReason_one: '{{count}} publication sans horaire : ajoutez une date pour lancer.',
      launchReason_other: '{{count}} publications sans horaire : ajoutez une date pour lancer.',
      launchFailed: 'La campagne n’a pas été lancée. Corrigez d’abord :',
      launchProblems: 'Problèmes de lancement',
      kpi: {
        planned: 'Planifiées',
        timed: 'Avec un horaire',
        published: 'Publiées',
        of: 'sur {{total}}',
        next: 'Prochaine publication : {{when}}',
        reachLater: 'La portée s’affichera après le lancement',
        reach: 'Portée : {{value}}',
      },
      notes: {
        paused: 'En pause : les horaires prévus sont conservés, et une publication déjà en cours se termine. Reprenez la campagne pour programmer à nouveau les publications.',
        active: 'En ligne depuis {{when}}.',
        completed: 'Toutes les publications sont parues.',
        completedFailed_one: 'Terminée avec {{count}} publication en échec. Modifiez-la pour la planifier à nouveau.',
        completedFailed_other: 'Terminée avec {{count}} publications en échec. Modifiez-les pour les planifier à nouveau.',
        canceled: 'Arrêtée : les publications qui n’étaient pas encore parues ne le seront pas.',
      },
      timeline: 'Calendrier',
      setTime: 'Définir l’horaire',
      actionFor: '{{action}} : {{title}}',
      view: 'Voir',
      copyOf: 'copie de la publication {{platform}}',
      publishing: 'Publication en cours',
      viewOn: 'Voir « {{title}} » sur {{platform}}',
      viewOnShort: 'Voir sur {{platform}}',
      empty: {
        title: 'Rien dans cette campagne pour l’instant',
        body: 'Ajoutez des publications et des stories avec l’heure de chacune. Elles sont vérifiées ensemble au lancement.',
        firstPost: 'Ajouter la première publication',
        firstStory: 'Ajouter la première story',
      },
      numbers: 'Chiffres publiés',
      confirm: {
        cancelTitle: 'Arrêter cette campagne ?',
        deleteTitle: 'Supprimer cette campagne ?',
        /** From the list, where the page does not say which (D-191). */
        cancelTitleNamed: 'Arrêter « {{name}} » ?',
        deleteTitleNamed: 'Supprimer « {{name}} » ?',
        cancelBody: 'Les publications qui ne sont pas encore parues seront annulées. Impossible de revenir en arrière.',
        deleteBody: 'La campagne et ses publications non parues sont supprimées. Les publications parues restent là où elles ont été publiées, et dans Publications.',
        keep: 'La garder',
      },
      edit: {
        title: 'Renommer la campagne',
        name: 'Nom',
        description: 'Description',
        cancel: 'Annuler',
        save: 'Enregistrer',
        nameNeeded: 'Saisissez un nom pour enregistrer.',
      },
    },
  },
  newAutomation: {
    namePlaceholder: 'ex. Réponse au mot-clé « prix »',
    nameNeeded: 'Donnez un nom pour ouvrir l’éditeur.',
    title: 'Nouvelle automatisation',
    scratch: 'Partir de zéro',
    scratchAside: 'Éditeur visuel · pour les automatisations sur mesure',
    nameLabel: 'Nom de l’automatisation',
    open: 'Ouvrir l’éditeur',
    creating: 'Création…',
    fromTemplate: 'Ou partir d’un modèle',
    seeAll: 'Voir les {{count}} modèles →',
  },
  goal: {
    tagNeeded: 'Choisissez un tag pour enregistrer.',
  },
  builderTest: {
    title: 'Tester avec un vrai contact',
    warning: 'Le test lance la <b>version publiée</b> maintenant : les messages sont réellement envoyés et les attentes réellement respectées. Un passage déjà en cours pour ce contact peut être remplacé.',
    unpublished: 'Vos modifications non publiées ne seront pas testées.',
    search: 'Rechercher un contact',
    group: 'Contact de test',
    loading: 'Chargement des contacts',
    noMatch: 'Aucun contact ne correspond.',
    none: 'Aucun contact pour l’instant.',
    run: 'Lancer le test',
    runFor: 'Lancer le test pour {{name}}',
    running: 'Test en cours…',
    error: 'Une erreur s’est produite. Veuillez réessayer.',
    finished_one: 'Terminé : {{count}} étape exécutée.',
    finished_other: 'Terminé : {{count}} étapes exécutées.',
    waitingUntil_one: 'En attente jusqu’au {{time}} : {{count}} étape pour l’instant.',
    waitingUntil_other: 'En attente jusqu’au {{time}} : {{count}} étapes pour l’instant.',
    waitingContact_one: 'En attente du contact : {{count}} étape pour l’instant.',
    waitingContact_other: 'En attente du contact : {{count}} étapes pour l’instant.',
    failed: 'Échec : {{error}}',
    failedAt: 'Échec à l’étape {{step}} : {{error}}',
    canceled: 'Annulé avant la fin.',
    stillRunning_one: 'Toujours en cours : {{count}} étape pour l’instant.',
    stillRunning_other: 'Toujours en cours : {{count}} étapes pour l’instant.',
    nothing: 'Rien n’a démarré pour ce contact : il est peut-être déjà dans un passage de cette automatisation, ou l’espace de travail a atteint sa limite mensuelle de déclenchements.',
    seeRuns: 'Voir les passages',
    enableFirst: 'Activez d’abord l’automatisation : un test lance la version publiée.',
    pickContact: 'Choisissez un contact pour lancer le test.',
  },
  builderBar: {
    neverPublished: 'Jamais publiée',
    upToDate: 'À jour : la version en ligne est celle que vous voyez',
    liveIsOlder: 'La version en ligne reste l’ancienne',
    test: 'Tester',
    enable: 'Activer',
    reactivate: 'Réactiver',
    working: 'En cours…',
    publish: 'Publier',
    publishChanges: 'Publier les modifications',
    publishing: 'Publication…',
    nothingToPublish: 'Aucune modification à publier',
    moreActions: 'Plus d’actions',
    disable: 'Désactiver',
    checksTitle: 'Vérifications',
    readyTitle: 'Prête à activer ?',
    toCheck_one: '{{count}} point à vérifier',
    toCheck_other: '{{count}} points à vérifier',
  },
  builderConfirm: {
    enableTitle: 'Activer « {{name}} » ?',
    reactivateTitle: 'Réactiver « {{name}} » ?',
    disableTitle: 'Désactiver « {{name}} » ?',
    createdViaAi: 'Brouillon créé via IA, relisez avant de publier',
    disableBody: 'Elle cesse de répondre à vos clients jusqu’à ce que vous la réactiviez.',
    trigger: 'Déclencheur',
    noTrigger: 'Aucun déclencheur configuré',
    steps: 'Étapes',
    stepCount_one: '{{count}} étape',
    stepCount_other: '{{count}} étapes',
    openChecks_one: '{{count}} vérification reste à faire :',
    openChecks_other: '{{count}} vérifications restent à faire :',
    consequence: 'Dès l’activation, elle répond aux vrais clients avec la version que vous voyez.',
    reactivateConsequence: 'La réactiver met aussi en ligne vos modifications non publiées : la version que vous voyez répond aux vrais clients.',
    cancel: 'Annuler',
    anyway: 'Activer quand même',
    disable: 'Désactiver',
    liveTitle: 'Votre automatisation est active',
    liveBody: 'Elle répond dès maintenant.',
    seeConversations: 'Voir les conversations',
    disabledTitle: 'Automatisation désactivée',
    disabledBody: 'Elle ne répond plus jusqu’à sa réactivation.',
  },
  triggerChannels: {
    unsupported_one: '{{only}} uniquement : décochez {{channels}}, qui ne peut pas déclencher cette automatisation.',
    unsupported_other: '{{only}} uniquement : décochez {{channels}}, qui ne peuvent pas déclencher cette automatisation.',
  },
  builderFields: {
    webhookUrlPlaceholder: 'ex. https://api.example.com/leads',
    firstNameFallbackPlaceholder: 'ex. cher client',
  },
  /** Builder blocks: the palette, the canvas nodes and the step settings (R06–R08). */
  nodes: {
    kind: { trigger: 'Déclencheur', condition: 'Condition', action: 'Action' },
    keyword_received: { label: 'Mot-clé reçu', description: 'Un message correspond à l’un de vos mots-clés' },
    message_received: { label: 'Message reçu', description: 'N’importe quel message reçu' },
    comment_received: { label: 'Commentaire reçu', description: 'Un commentaire reçu, sur n’importe quelle publication ou sur une seule' },
    story_reply_received: { label: 'Réponse à une story', description: 'Quelqu’un a répondu à l’une de vos stories, n’importe laquelle ou une seule' },
    story_mention_received: { label: 'Mention en story', description: 'Quelqu’un vous a mentionné dans sa story' },
    live_comment_received: { label: 'Commentaire en live', description: 'Un commentaire pendant l’un de vos lives, n’importe lequel ou avec un mot-clé' },
    contact_created: { label: 'Nouveau contact', description: 'Un contact est créé' },
    tag_added: { label: 'Tag ajouté', description: 'Un tag est ajouté à un contact' },
    tag_removed: { label: 'Tag retiré', description: 'Un tag est retiré d’un contact' },
    manual_trigger: { label: 'Déclenchement manuel', description: 'Lancé par un membre de l’équipe' },
    inbound_webhook: { label: 'Webhook', description: 'Lancé par un appel HTTP externe' },
    send_message: { label: 'Envoyer un message', description: 'Répondre dans la conversation' },
    reply_comment: { label: 'Répondre au commentaire', description: 'Répondre publiquement au commentaire qui a lancé l’automatisation' },
    react_to_message: { label: 'Réagir avec un cœur', description: 'Un cœur sur le message qui a lancé l’automatisation' },
    add_tag: { label: 'Ajouter un tag', description: 'Taguer le contact' },
    remove_tag: { label: 'Retirer un tag', description: 'Retirer un tag au contact' },
    set_custom_field: { label: 'Remplir un champ personnalisé', description: 'Écrire une valeur dans un champ' },
    send_email: { label: 'Envoyer un e-mail', description: 'Écrire un e-mail au contact' },
    wait: { label: 'Attendre', description: 'Faire une pause avant de continuer' },
    http_request: { label: 'Requête HTTP', description: 'Appeler une API externe' },
    goto_automation: { label: 'Aller à une automatisation', description: 'Continuer dans une autre automatisation' },
    stop: { label: 'Arrêter', description: 'Terminer l’automatisation ici' },
    has_tag: { label: 'A le tag', description: 'Le contact a-t-il ce tag ?' },
    not_has_tag: { label: 'N’a pas le tag', description: 'Ce tag manque-t-il au contact ?' },
    field_equals: { label: 'Champ égal à', description: 'Un champ est-il égal à une valeur ?' },
    field_contains: { label: 'Champ contient', description: 'Un champ contient-il un texte ?' },
    field_greater_than: { label: 'Champ supérieur à', description: 'Un champ numérique dépasse-t-il une valeur ?' },
    field_less_than: { label: 'Champ inférieur à', description: 'Un champ numérique est-il sous une valeur ?' },
    channel_is: { label: 'Canal', description: 'Le message est-il arrivé sur un canal donné ?' },
    has_previous_interaction: { label: 'Interaction précédente', description: 'Le contact a-t-il déjà interagi ?' },
    follows_account: { label: 'Vous suit', description: 'Le contact suit-il votre compte connecté ?' },
    message_contains_keyword: { label: 'Message contenant un mot-clé', description: 'Le message qui a lancé l’automatisation contient-il un mot-clé ?' },
  },
  /** What a step still needs, and why a block cannot go somewhere (D-105, D-141). */
  nodeProblems: {
    keyword: 'Ajoutez au moins un mot-clé',
    tag: 'Choisissez un tag',
    field: 'Choisissez un champ',
    number: 'Saisissez un nombre à comparer',
    value: 'Saisissez une valeur à comparer',
    messageText: 'Écrivez le texte du message',
    replyText: 'Écrivez le texte de la réponse',
    customField: 'Choisissez un champ personnalisé',
    subject: 'Ajoutez un objet',
    body: 'Écrivez le corps de l’e-mail',
    wait: 'Indiquez combien de temps attendre',
    url: 'Saisissez une URL http(s) complète',
    automation: 'Choisissez l’automatisation de destination',
    channel: 'Choisissez un canal',
    needsMessageTrigger: 'Fonctionne seulement après un déclencheur Message reçu, Mot-clé reçu, Réponse à une story ou Mention en story',
    channelOnly: '{{only}} uniquement. Cette automatisation fonctionne sur {{channels}}.',
    channelGap: '{{only}} uniquement : sur {{missing}}, elle répond toujours Non. Ajoutez une condition « Canal » avant elle, ou limitez le déclencheur.',
  },
  /** The second line of a canvas node. */
  nodeSummary: {
    post: 'publication {{id}}',
    story: 'story {{id}}',
  },
  builderDuration: {
    seconds_one: '{{count}} seconde',
    seconds_other: '{{count}} secondes',
    minutes_one: '{{count}} minute',
    minutes_other: '{{count}} minutes',
    hours_one: '{{count}} heure',
    hours_other: '{{count}} heures',
    days_one: '{{count}} jour',
    days_other: '{{count}} jours',
  },
  /** The automation editor (R06–R08): page, top bar and toasts. */
  builderPage: {
    loading: 'Chargement de l’automatisation',
    loadError: 'Impossible de charger cette automatisation. Elle a peut-être été supprimée.',
    backToList: 'Retour aux automatisations',
    back: 'Automatisations',
    name: 'Nom de l’automatisation',
    fixIssues: 'Corrigez les problèmes signalés avant d’activer cette automatisation.',
    changeFailed: 'La modification a échoué',
    publishedTitle: 'Modifications publiées',
    publishedBody: 'Les nouveaux déclenchements utilisent la version que vous venez d’enregistrer.',
    unsavedTitle: 'Vos dernières modifications n’ont pas été enregistrées',
    unsavedBody: 'La connexion a été coupée pendant l’enregistrement.',
    renameFailed: 'Impossible de renommer l’automatisation',
    tryAgain: 'Veuillez réessayer.',
    inspector: 'Vérifications et test',
    stepPanel: 'Réglages de l’étape',
    deletedStep: 'une étape supprimée',
  },
  builderPalette: {
    label: 'Blocs',
    search: 'Rechercher un bloc',
    close: 'Fermer les blocs',
    hint: 'Glissez un bloc sur le canevas ou cliquez sur « + » entre deux étapes.',
    groups: {
      trigger: 'Déclencheurs',
      condition: 'Conditions',
      action: 'Actions',
    },
    triggersFolded: 'Repliés : cette automatisation a déjà son déclencheur.',
    noMatch: 'Aucun bloc ne correspond à « {{query}} ».',
    unavailable: '{{label}} — {{reason}}',
  },
  builderCanvas: {
    label: 'Canevas',
    zoom: 'Zoom',
    zoomIn: 'Zoom avant',
    zoomOut: 'Zoom arrière',
    fit: 'Ajuster à l’écran',
    minimap: 'Mini-carte',
    addStep: 'Ajouter une étape',
    addStepBetween: 'Ajouter une étape entre {{from}} et {{to}}',
    aStep: 'une étape',
    needsAttention: 'À vérifier : {{problems}}',
    yes: 'Oui',
    no: 'Non',
    next: 'Suivant',
    noTap: 'Sans appui',
    button: 'Bouton',
    nodeLabel: '{{kind}} : {{title}}',
    edgeLabel: 'De « {{from}} » vers « {{to}} »',
    edgeLabelBranch: 'De « {{from}} » vers « {{to}} », {{branch}}',
    skipTo: 'Aller au canevas',
    a11y: {
      node: 'Entrée ou Espace pour choisir une étape, Suppr pour la retirer, les flèches pour la déplacer.',
      nodeNoKeys: 'Entrée ou Espace pour choisir une étape.',
      edge: 'Entrée ou Espace pour choisir une liaison, Suppr pour la retirer.',
      handle: 'Point de liaison',
      moved: 'Étape déplacée vers {{direction}}. Position : x {{x}}, y {{y}}',
      directions: { up: 'le haut', down: 'le bas', left: 'la gauche', right: 'la droite' },
    },
  },
  /** The editor on a phone: the steps as a list, edited through sheets (D-135). */
  builderFlow: {
    hint: 'Touchez une étape pour la modifier. Pour relier ou déplacer des étapes, utilisez l’éditeur visuel.',
    openEditor: 'Ouvrir l’éditeur',
    addTrigger: 'Ajouter un déclencheur',
    notConnected: 'Non reliées',
    steps: 'Étapes de l’automatisation',
    edit: 'Modifier {{title}}',
    editNeeds: 'Modifier {{title}} — à vérifier : {{problems}}',
    addBefore: 'Ajouter une étape avant {{title}}',
    addAfter: 'Ajouter une étape après {{title}}',
    ifYes: 'Si oui',
    ifNo: 'Si non',
    ifTap: 'Si la personne appuie sur « {{label}} »',
    theButton: 'le bouton',
    ifNoTap: 'Si personne n’appuie',
    stepsButton: 'Étapes',
    addStep: 'Ajouter une étape',
    noFit: 'Aucune étape ne convient ici.',
  },
  /** The checklist beside the canvas: Vérifications / Prête à activer ? (R06–R08). */
  builderChecks: {
    hint: 'Cliquez sur un bloc pour le configurer.',
    done: '(fait)',
    todo: '(à faire)',
    addTrigger: 'Ajoutez un déclencheur',
    addTriggerDetail: 'Glissez-en un depuis la gauche : il décide quand cette automatisation se lance.',
    needsSetup: '{{name}} : à configurer',
    triggerOk: 'Déclencheur configuré',
    stray: '{{name}} : étape non reliée',
    strayDetail: 'Reliez-la à l’automatisation ou supprimez-la : en l’état, elle ne se lance jamais.',
    connected: 'Toutes les étapes sont reliées',
    stepsAfter_one: '{{count}} étape après le déclencheur',
    stepsAfter_other: '{{count}} étapes après le déclencheur',
    branches: 'Oui → {{yes}} · Non → {{no}}',
    end: 'fin',
    needsAttention: '{{name}} : à vérifier',
    needsFallback: '{{name}} : valeur de repli manquante',
    fallbackDetail: 'Les contacts sans {{fields}} verraient un blanc.',
    channelOk: 'Canal connecté',
    connect: 'Connectez un canal',
    connectDetail: 'Rien n’atteint cette automatisation tant qu’aucun canal n’est connecté.',
  },
  /** A selected step’s settings. */
  builderConfig: {
    title: 'Titre de l’étape',
    delete: 'Supprimer l’étape',
    tag: 'Tag',
    chooseTag: 'Choisissez un tag…',
    keywords: 'Mots-clés',
    onePerLine: 'Un par ligne',
    channels: 'Canaux',
    channelsHint: 'Laissez tout décoché pour accepter tous les canaux',
    fieldSource: 'Source du champ',
    contactField: 'Champ du contact',
    customField: 'Champ personnalisé',
    field: 'Champ',
    chooseField: 'Choisissez un champ…',
    value: 'Valeur',
    buttons: 'Boutons (3 max)',
    buttonLabel: 'Libellé du bouton {{n}}',
    buttonLabelPlaceholder: 'ex. Réserver un appel',
    buttonLink: 'Lien du bouton {{n}}',
    buttonLinkPlaceholder: 'ex. https://exemple.com',
    buttonLinkHint: 'Laissez vide pour créer une branche.',
    removeButton: 'Retirer le bouton',
    addButton: 'Bouton',
    buttonBranch: 'Un bouton sans lien devient une branche à relier sur le canevas.',
    match: 'Correspondance',
    matchExact: 'Le message est égal au mot-clé',
    matchContains: 'Le message contient le mot-clé',
    matchStartsWith: 'Le message commence par le mot-clé',
    liveHint: 'Laissez vide pour répondre à chaque commentaire en live. Un mot-clé est reconnu n’importe où dans le commentaire.',
    replyText: 'Texte de la réponse',
    replyHint: 'Publiée sous le commentaire qui a lancé cette automatisation. Ne fonctionne qu’après un déclencheur de commentaire.',
    contactCreatedNote: 'Se lance à chaque nouveau contact dans cet espace de travail. Rien à configurer.',
    manualNote: 'Lancé par un membre de l’équipe depuis une fiche contact. Rien à configurer.',
    webhookNote: 'Se lance quand un système externe appelle l’URL webhook de cette automatisation (disponible une fois activée).',
    messageText: 'Texte du message',
    subject: 'Objet',
    body: 'Corps',
    choose: 'Choisissez…',
    yes: 'Oui',
    no: 'Non',
    method: 'Méthode',
    url: 'URL',
    requestBodyHint: 'Facultatif. Les variables sont acceptées.',
    continueIn: 'Continuer dans',
    chooseAutomation: 'Choisissez une automatisation…',
    stopNote: 'Termine l’automatisation pour ce contact. Rien à configurer.',
    channel: 'Canal',
    withinDays: 'Au cours des derniers (jours)',
    withinDaysHint: 'Laissez vide pour n’importe quand',
    reactNote: 'Met un cœur sur le message qui a lancé cette automatisation : un message, un mot-clé ou une réponse à une story. Instagram n’autorise que le cœur et ne peut pas réagir aux commentaires.',
    followsNote: 'Quand l’automatisation arrive à cette étape, le canal indique si le contact suit votre compte. Instagram répond ; un canal qui ne peut pas le dire compte comme « non ».',
    unknown: 'Type d’étape inconnu.',
  },
  builderEditors: {
    contactFields: {
      first_name: 'Prénom',
      last_name: 'Nom',
      full_name: 'Nom complet',
      email: 'E-mail',
      phone: 'Téléphone',
      username: 'Nom d’utilisateur',
    },
    waitFor: 'Attendre',
    waitLess: 'Attendre moins',
    waitMore: 'Attendre plus',
    waitDuration: 'Durée d’attente',
    unit: 'Unité',
    minutes: 'Min',
    hours: 'Heures',
    days: 'Jours',
    waitSentence: 'Le contact attend {{duration}} avant l’étape suivante.',
    variable: 'Variable',
    insertVariable: 'Insérer une variable',
    contactGroup: 'Contact',
    customFieldsGroup: 'Champs personnalisés',
    fallbackIf: 'Valeur de repli si {{field}} est vide',
    preview: 'Aperçu',
    previewEmpty: 'Votre message apparaît ici.',
    /** A made-up contact the preview shows where the text uses a name. */
    sample: { first_name: 'Sarra', last_name: 'Ben Ali', full_name: 'Sarra Ben Ali' },
    sampleNote: 'Aperçu pour un contact fictif, {{name}}.',
  },
  /** The comment and story-reply triggers pinned to one post or story (D-103, D-117). */
  builderPins: {
    account: 'Compte',
    idPlaceholder: 'ex. 17900000000000000',
    onlyPost: 'Uniquement cette publication',
    postHintNoChannel: 'Connectez un canal pour choisir parmi ses publications, ou collez l’identifiant d’une publication. Vide : les commentaires sur n’importe quelle publication.',
    postHintCannotList: 'Ce canal ne peut pas lister ses publications : collez plutôt l’identifiant. Vide : les commentaires sur n’importe quelle publication.',
    orScheduled: 'Ou une publication programmée',
    orScheduledHint: 'L’automatisation s’active quand la publication est publiée.',
    none: 'Aucune',
    postHint: 'Réagit seulement aux commentaires de la publication choisie. Une publication programmée s’active quand elle est publiée. « N’importe quelle publication » accepte les commentaires partout.',
    loadingPosts: 'Chargement des publications…',
    anyPost: 'N’importe quelle publication',
    scheduledGroup: 'Programmées : actives une fois publiées',
    publishedGroup: 'Publiées',
    currentlyPinned: 'Épinglée actuellement : {{id}}',
    deletedScheduled: 'Publication programmée supprimée',
    untitled: 'Publication sans légende',
    unpinned: 'Désépinglé : ce déclencheur répond maintenant aux commentaires de toutes les publications du compte.',
    pinLoading: 'Chargement de la publication…',
    pinDeleted: 'La publication programmée a été supprimée : ce déclencheur ne s’activera jamais.',
    pinLoadError: 'Impossible de charger la publication : {{error}}',
    pinPublished: 'Publiée : ce déclencheur répond à ses commentaires.',
    pinPublishedAt: 'Publiée {{at}} : ce déclencheur répond à ses commentaires.',
    pinNoId: 'Publiée, mais son identifiant n’a pas été enregistré : ses commentaires ne peuvent pas être reconnus. Épinglez plutôt la publication publiée.',
    pinPublishing: 'Publication en cours : s’active dans un instant.',
    pinFailed: 'La publication n’a pas pu être publiée : relancez-la ou choisissez-en une autre, sinon ce déclencheur ne s’activera jamais.',
    pinWaiting: 'S’active quand la publication est publiée.',
    pinWaitingAt: 'S’active quand la publication est publiée ({{at}}).',
    openPost: 'Ouvrir la publication',
    onlyStory: 'Uniquement cette story',
    storyHintNoChannel: 'Connectez un canal pour choisir parmi ses stories, ou collez l’identifiant d’une story. Vide : les réponses à n’importe quelle story.',
    storyHintCannotList: 'Ce canal ne peut pas lister ses stories : collez plutôt l’identifiant. Vide : les réponses à n’importe quelle story.',
    storyHint: 'Réagit seulement aux réponses à la story choisie. Une story dure 24 heures ; « N’importe quelle story » continue de répondre après son expiration.',
    loadingStories: 'Chargement des stories…',
    anyStory: 'N’importe quelle story',
    storyGone: 'La story épinglée n’est plus en ligne sur ce compte : personne ne peut plus y répondre. Choisissez-en une en ligne, ou « N’importe quelle story ».',
    videoStory: 'Story vidéo',
    story: 'Story',
  },
  builderUndo: {
    steps_one: 'Étape supprimée',
    steps_other: '{{count}} étapes supprimées',
    connections_one: 'Connexion supprimée',
    connections_other: '{{count}} connexions supprimées',
    hint: '{{keys}} annule aussi la suppression.',
    undo: 'Annuler',
  },
  starters: {
    questionPlaceholder: 'ex. Quels sont vos horaires d’ouverture ?',
    menuItemPlaceholder: 'ex. Tarifs',
    back: 'Canaux',
    title: 'Amorces & menu',
    lede: 'Un moyen en un geste de démarrer la conversation, et un menu accessible à tout moment.',
    startersTitle: 'Amorces de conversation',
    startersBody: 'Affichées à la première ouverture du chat. Un appui lance l’automatisation choisie.',
    menuTitle: 'Menu',
    menuBody: 'Toujours disponible dans le chat. Chaque entrée lance une automatisation ou ouvre un lien.',
    getStarted: 'Messenger n’affiche le menu d’une Page qu’avec un bouton « Démarrer » : publier un menu en ajoute un.',
    countLabel: '{{count}} sur {{max}} utilisées',
    starter: {
      text: 'Texte de l’amorce {{n}}',
      automation: 'Automatisation lancée par l’amorce {{n}}',
      more: 'Plus d’actions pour l’amorce {{n}}',
      handle: 'Déplacer l’amorce {{n}}',
      add: 'Ajouter une amorce',
      none: 'Aucune amorce pour l’instant.',
      missing: 'Sans automatisation, ce bouton ne fera rien.',
      moved: 'Amorce déplacée en position {{n}} sur {{total}}.',
    },
    menuItem: {
      text: 'Libellé de l’entrée {{n}}',
      action: 'Type d’action de l’entrée {{n}}',
      automation: 'Automatisation lancée par l’entrée {{n}}',
      link: 'Lien de l’entrée {{n}}',
      more: 'Plus d’actions pour l’entrée {{n}}',
      handle: 'Déplacer l’entrée {{n}}',
      add: 'Ajouter une entrée',
      none: 'Aucune entrée pour l’instant.',
      missing: 'Sans automatisation, cette entrée ne fera rien.',
      moved: 'Entrée déplacée en position {{n}} sur {{total}}.',
    },
    starts: 'lance',
    opens: 'ouvre un lien',
    chooseAutomation: 'Choisir une automatisation…',
    notEnabled: '{{name}} (non activée)',
    moveUp: 'Monter',
    moveDown: 'Descendre',
    remove: 'Supprimer',
    dragHint: 'Glisser pour réordonner, ou utiliser les flèches du clavier',
    atMost: '{{app}} en affiche {{max}} au maximum.',
    preview: 'Aperçu',
    previewIn: 'Aperçu dans {{app}}',
    beforeFirst: 'Avant le premier message',
    noStarters: 'Aucune amorce',
    noMenu: 'Aucun menu',
    unlinked: 'Non relié',
    unpublished: 'Modifications non publiées',
    lastPublished: 'Dernière publication : {{when}}',
    notPublished: 'Pas encore publié',
    noChangesShort: 'aucune modification',
    discard: 'Annuler les modifications',
    publish: 'Enregistrer et publier',
    publishing: 'Publication…',
    published: 'Publié sur {{app}}',
    refused: 'Enregistré, mais {{app}} l’a refusé',
    lastRefused: '{{app}} a refusé la dernière mise à jour : {{error}}',
    unsupported: 'Ce canal n’a ni amorces ni menu.',
  },
  channels: {
    title: 'Canaux',
    lede: 'Les comptes sur lesquels vos automatisations écoutent et répondent.',
    catalog: {
      instagram: 'DM, réponses aux stories et commentaires, pour un compte professionnel.',
      messenger: 'Les conversations avec les personnes qui écrivent à votre Page Facebook.',
    },
    other: 'Autres comptes connectés',
    connect: 'Connecter',
    redirecting: 'Redirection…',
    connectAnother: {
      instagram: 'Connecter un autre compte Instagram',
      messenger: 'Connecter une autre Page',
      other: 'Connecter un autre compte',
    },
    since: 'Depuis le {{date}}',
    pageSince: 'Page Facebook · depuis le {{date}}',
    grant: 'Reconnectez pour autoriser {{list}}.',
    and: '{{first}} et {{last}}',
    accessExpired: 'Accès expiré le {{date}}',
    accessExpires: 'L’accès expire le {{date}}',
    renew: 'Renouveler',
    reconnect: 'Reconnecter',
    disconnect: 'Déconnecter',
    startersMenu: 'Amorces & menu',
    actionsFor: 'Plus d’actions pour {{name}}',
    planLimit_one: '{{count}} canal connecté sur {{max}} inclus dans l’offre {{plan}}',
    planLimit_other: '{{count}} canaux connectés sur {{max}} inclus dans l’offre {{plan}}',
    seePlan: 'Voir l’offre',
    adminsOnly: 'Seuls les administrateurs peuvent connecter ou déconnecter des canaux. Vous voyez ce qui est connecté.',
    notConfigured: 'Ce canal n’est pas encore configuré sur ce serveur : les identifiants de l’application manquent dans l’environnement de déploiement. Voir docs/DEPLOYMENT.md §11.',
    connected: '{{channel}} connecté',
    connectedBody: 'Les messages reçus sur ce compte arrivent maintenant dans Conversations et dans vos automatisations.',
    connectError: {
      title: 'Impossible de connecter {{channel}}',
      denied: 'L’accès n’a pas été accordé. Cliquez sur Connecter et acceptez les autorisations demandées pour continuer.',
      state_invalid: 'Ce lien de connexion n’est pas valide. Cliquez sur Connecter pour recommencer.',
      state_expired: 'La connexion a pris plus de 10 minutes. Cliquez sur Connecter pour recommencer.',
      state_foreign: 'Cette connexion a été lancée depuis un autre compte ou un autre espace. Cliquez sur Connecter pour lancer la vôtre.',
      no_account: 'Aucun compte connectable n’a été trouvé. Utilisez un compte Instagram professionnel ou une Page Facebook que vous gérez.',
      provider: '{{channel}} a refusé l’autorisation. Cliquez sur Connecter pour réessayer.',
      in_use: 'Ce compte est déjà connecté à un autre espace. Déconnectez-le d’abord là-bas.',
      limit: 'Votre offre n’a plus de place pour un autre canal.',
      failed: 'La connexion n’a pas abouti. Cliquez sur Connecter pour réessayer.',
    },
    disconnected: 'Canal déconnecté',
    opening: 'Ouverture de {{channel}}…',
    openingBody: 'La page d’autorisation s’ouvre dans quelques secondes. Ne touchez pas l’écran d’ici là : sur un téléphone ou une tablette, un appui confierait la page à l’app {{channel}} plutôt qu’à votre navigateur, et chaque appui relance l’attente.',
    cancel: 'Annuler',
    disconnectTitle: 'Déconnecter {{name}} ?',
    disconnectBody: 'Les nouveaux messages vers ce compte n’arriveront plus dans Conversations ni dans vos automatisations. Les conversations et les contacts existants restent. Vous pouvez reconnecter à tout moment.',
    disconnecting: 'Déconnexion…',
    picker: {
      title: {
        messenger: 'Quelles Pages voulez-vous connecter ?',
        other: 'Quels comptes voulez-vous connecter ?',
      },
      list: {
        messenger: 'Pages à connecter',
        other: 'Comptes à connecter',
      },
      body: {
        messenger: 'Chaque Page cochée devient un canal. Vous pourrez connecter les autres plus tard.',
        other: 'Chaque compte coché devient un canal. Vous pourrez connecter les autres plus tard.',
      },
      count: {
        messenger_one: '{{count}} Page',
        messenger_other: '{{count}} Pages',
        other_one: '{{count}} compte',
        other_other: '{{count}} comptes',
      },
      done: {
        messenger_one: '{{count}} Page connectée',
        messenger_other: '{{count}} Pages connectées',
        other_one: '{{count}} compte connecté',
        other_other: '{{count}} comptes connectés',
      },
      expired: 'Cette autorisation {{channel}} n’est plus disponible. Elle dure 10 minutes. Cliquez à nouveau sur Connecter pour choisir.',
      close: 'Fermer',
      connectedBody: 'Les messages reçus arrivent maintenant dans Conversations et dans vos automatisations.',
      noRoom: 'Votre offre n’a plus de place pour un autre canal.',
      room_one: 'Votre offre a encore de la place pour {{count}} canal.',
      room_other: 'Votre offre a encore de la place pour {{count}} canaux.',
      untick: 'Décochez {{what}} ou',
      seePlan: 'voyez votre offre',
      cancel: 'Annuler',
      connecting: 'Connexion…',
      connect: 'Connecter',
      connectSome: 'Connecter {{what}}',
      heldElsewhere: 'Connecté dans un autre espace de travail',
      connectedHere: 'Déjà connecté',
    },
  },
  settings: {
    title: 'Paramètres',
    lede: 'Équipe, conversations, données contacts, facturation et accès développeurs.',
    ledeFor: 'Équipe, conversations, données contacts, facturation et accès développeurs de {{name}}.',
    loading: 'Chargement',
    adminsOnly: 'Seuls les administrateurs peuvent modifier ce réglage.',
  },
  developers: {
    adminsOnly: 'Seuls les administrateurs peuvent créer, voir et révoquer les tokens d’API.',
    createTitle: 'Créer un token d’API',
    intro: 'Un token permet à votre assistant IA de se connecter à cet espace de travail. Il agit avec les droits de la personne qui l’a créé, jamais au-delà.',
    name: 'Nom du token',
    namePlaceholder: 'ex. Claude Code sur mon poste',
    nameNeeded: 'Saisissez un nom pour le token.',
    scopes: 'Permissions',
    scopeNeeded: 'Cochez au moins une permission.',
    scope: {
      read: 'Lecture',
      write: 'Écriture',
    },
    scopeHint: {
      read: 'Lire les canaux, les automatisations et leurs statistiques.',
      write: 'Créer des brouillons d’automatisation, seulement si votre rôle est administrateur au moment de l’appel. Un brouillon attend votre relecture avant toute publication.',
    },
    lifetime: 'Durée de validité',
    days_one: '{{count}} jour',
    days_other: '{{count}} jours',
    create: 'Créer le token',
    creating: 'Création…',
    limitReached: '10 tokens actifs au maximum : révoquez-en un pour en créer un autre.',
    createFailed: 'Le token n’a pas pu être créé. Réessayez.',
    listTitle: 'Tokens d’API',
    list: 'Tokens de l’espace de travail',
    empty: 'Aucun token pour l’instant.',
    createdBy: 'Créé par {{name}} le {{date}}',
    lastUsed: 'Dernier usage le {{date}}',
    neverUsed: 'Jamais utilisé',
    expiresOn: 'Expire le {{date}}',
    expiredOn: 'Expiré le {{date}}',
    revokedOn: 'Révoqué le {{date}}',
    state: {
      active: 'Actif',
      expired: 'Expiré',
      revoked: 'Révoqué',
    },
    revoke: 'Révoquer',
    revokeLabel: 'Révoquer le token {{name}}',
    revokeTitle: 'Révoquer « {{name}} » ?',
    revokeBody: 'L’assistant qui utilise ce token perd l’accès tout de suite. Cette action est définitive.',
    revokeConfirm: 'Révoquer le token',
    revokeFailed: 'Le token n’a pas pu être révoqué. Réessayez.',
    cancel: 'Annuler',
    created: {
      title: 'Token créé',
      label: 'Votre nouveau token',
      warning: 'Ce token ne sera plus affiché. Copiez-le maintenant.',
      copy: 'Copier',
      copied: 'Copié',
      copyFailed: 'La copie a échoué : sélectionnez le token et copiez-le.',
      done: 'J’ai copié le token',
    },
    connect: {
      title: 'Connecter votre assistant IA',
      intro: 'Votre assistant se connecte à Repliq par le protocole MCP, avec un token créé sur cette page. Dans les exemples, remplacez l’espace réservé par ce token.',
      serverUrl: 'URL du serveur',
      copyUrl: 'Copier l’URL',
      copy: 'Copier',
      copied: 'Copié',
      copyFailed: 'La copie a échoué : sélectionnez le texte et copiez-le.',
      claudeCode: {
        title: 'Claude Code',
        cli: 'Dans un terminal :',
        json: 'Ou dans le fichier .mcp.json d’un projet, le token étant dans la variable d’environnement REPLIQ_MCP_TOKEN :',
        noOAuth: 'Avec un en-tête Authorization configuré, Claude Code utilise ce token et ne bascule pas sur OAuth.',
      },
      api: {
        title: 'API Messages d’Anthropic (connecteur MCP)',
        body: 'Ajoutez cet en-tête bêta et ces deux champs à votre requête vers /v1/messages :',
        limits: 'Seuls les appels d’outils sont pris en charge, et l’URL du serveur doit être publique et commencer par https://.',
      },
      mcpRemote: {
        title: 'Claude Desktop et clients configurés en JSON (mcp-remote)',
        body: 'mcp-remote relaie un client local vers ce serveur. Mettez l’en-tête dans un fichier d’une ligne, pour garder le token hors des arguments du processus :',
        file: 'Contenu du fichier d’en-têtes',
        config: 'Configuration du client (claude_desktop_config.json pour Claude Desktop)',
        windows: 'Sous Windows, Claude Desktop n’échappe pas les espaces des arguments. Variante donnée par mcp-remote, sans espace autour des deux-points :',
      },
      connectors: {
        title: 'Connecteurs personnalisés de claude.ai et Claude Desktop',
        step1: 'Dans Claude, ouvrez Customize › Connectors, ajoutez un connecteur personnalisé et saisissez l’URL du serveur.',
        step2: 'Authentication : choisissez No sign-in.',
        step3: 'Request headers : ajoutez l’en-tête ci-dessous. Claude envoie la valeur telle quelle, sans ajouter de schéma : gardez « Bearer » et l’espace.',
        headerName: 'Nom',
        headerValue: 'Valeur',
        beta: 'Bêta : l’authentification par en-têtes de requête n’est ouverte qu’à un nombre limité d’organisations. Si la section Request headers n’apparaît pas, votre organisation n’y a pas encore accès.',
        shared: 'Sur un plan Team ou Enterprise, un Owner ajoute le connecteur pour toute l’organisation. Tous les membres utilisent alors le même token, et agissent donc avec les droits de la personne qui l’a créé dans Repliq.',
        oauth: 'Sans accès à cette bêta, claude.ai et Claude Desktop exigent OAuth pour un connecteur distant.',
      },
      oauthV2: 'OAuth (rpqo_) : prévu en v2, nécessaire pour les clients non techniques.',
    },
  },
  people: {
    emailPlaceholder: 'ex. collegue@entreprise.com',
    inviteTitle: 'Inviter un membre',
    email: 'Adresse e-mail',
    role: 'Rôle',
    roles: { owner: 'Propriétaire', admin: 'Admin', agent: 'Agent' },
    send: 'Envoyer l’invitation',
    sending: 'Envoi…',
    emailNeeded: 'Saisissez une adresse e-mail pour inviter.',
    hint: {
      agent: 'Les agents gèrent les conversations et les contacts. Ils ne peuvent pas modifier les automatisations, les canaux ni la facturation.',
      admin: 'Les admins créent aussi les automatisations, connectent les canaux, invitent des membres et voient les factures.',
    },
    sent: 'Invitation envoyée à {{email}}',
    members: 'Membres',
    membersOf_one: '{{count}} membre sur {{max}}',
    membersOf_other: '{{count}} membres sur {{max}}',
    membersCount_one: '{{count}} membre',
    membersCount_other: '{{count}} membres',
    seatsOf_one: '{{count}} siège Conversations sur {{max}}',
    seatsOf_other: '{{count}} sièges Conversations sur {{max}}',
    seatsCount_one: '{{count}} siège Conversations',
    seatsCount_other: '{{count}} sièges Conversations',
    seePlan: 'Voir l’offre',
    you: '(vous)',
    seat: 'Siège Conversations',
    pendingTitle: 'Invitations en attente',
    noPending: 'Aucune invitation en attente.',
    invitedAs: 'Invité comme {{role}} · expire le {{date}}',
    revoke: 'Révoquer',
    revokeLabel: 'Révoquer l’invitation de {{email}}',
    /** Every seat taken (members + pending invitations, D-183): the form is blocked before sending. */
    seatsFull_one: 'Votre offre {{planName}} est limitée à {{limit}} utilisateur. Libérez une place ou changez d’offre pour inviter quelqu’un.',
    seatsFull_other: 'Votre offre {{planName}} est limitée à {{limit}} utilisateurs. Libérez une place ou changez d’offre pour inviter quelqu’un.',
  },
  inboxSettings: {
    title: 'Pause des automatisations quand un membre répond',
    saved: 'Enregistré automatiquement',
    body: 'Quand un membre répond depuis Conversations, les automatisations se retirent pour ne jamais parler en même temps que lui. Les réponses envoyées depuis l’app Instagram ou Messenger ne comptent pas. Clôturer la conversation, ou cliquer sur Reprendre, lève la pause tout de suite.',
    length: 'Durée de la pause',
    never: 'Ne pas mettre en pause',
    default: '{{label}} (par défaut)',
    minutes: '{{count}} min',
    hours_one: '{{count}} h',
    hours_other: '{{count}} h',
    days_one: '{{count}} jour',
    days_other: '{{count}} jours',
    week: '1 semaine',
    example: 'Exemple : si un membre répond à 14:00, les automatisations restent silencieuses pour ce contact jusqu’à {{until}}.',
    exampleNever: 'Les réponses ne mettent rien en pause : les automatisations continuent de répondre à côté de votre équipe.',
    nextDay: '{{time}} le lendemain',
    weekLater: '{{time}} une semaine plus tard',
    daysLater: '{{time}} {{count}} jours plus tard',
    saveFailed: 'Impossible d’enregistrer la durée de la pause',
  },
  tags: {
    namePlaceholder: 'ex. Nouveau lead',
    nameNeeded: 'Saisissez un nom pour créer le tag.',
    createTitle: 'Créer un tag',
    name: 'Nom du tag',
    create: 'Créer le tag',
    creating: 'Création…',
    created: 'Tag « {{name}} » créé',
    preview: 'Aperçu',
    colour: 'Couleur',
    colours: { slate: 'Ardoise', violet: 'Violet', blue: 'Bleu', green: 'Vert', amber: 'Ambre', pink: 'Rose', red: 'Rouge', teal: 'Sarcelle', orange: 'Orange' },
    list: 'Tags',
    contacts_one: '{{count}} contact',
    contacts_other: '{{count}} contacts',
    usedIn_one: 'utilisé dans {{count}} automatisation',
    usedIn_other: 'utilisé dans {{count}} automatisations',
    rename: 'Renommer',
    renameLabel: 'Renommer {{name}}',
    delete: 'Supprimer',
    deleteLabel: 'Supprimer {{name}}',
    emptyTitle: 'Aucun tag pour l’instant',
    emptyBody: 'Des tags comme « Nouveau lead » ou « VIP » filtrent les contacts et orientent les conditions des automatisations.',
    deleteTitle: 'Supprimer « {{name}} » ?',
    deleteContacts_one: 'Le tag est retiré de {{count}} contact.',
    deleteContacts_other: 'Le tag est retiré de {{count}} contacts.',
    deleteAutomations_one: 'L’automatisation qui l’utilise ne le trouvera plus.',
    deleteAutomations_other: 'Les {{count}} automatisations qui l’utilisent ne le trouveront plus.',
    cannotUndo: 'Impossible de revenir en arrière.',
    cancel: 'Annuler',
    deleting: 'Suppression…',
    confirmDelete: 'Supprimer le tag',
    deleted: 'Tag supprimé',
    deletedBody: 'Il a été retiré de tous les contacts qui l’avaient.',
    editTitle: 'Renommer le tag',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
  },
  fields: {
    labelPlaceholder: 'ex. Produit d’intérêt',
    optionsPlaceholder: 'ex. Basique\nPremium',
    nameNeeded: 'Saisissez un nom pour ajouter le champ.',
    /** The key preview no longer comes from the placeholder (R34). */
    keyPending: 'La clé technique est générée quand vous saisissez un nom.',
    addTitle: 'Ajouter un champ',
    label: 'Nom du champ',
    add: 'Ajouter le champ',
    adding: 'Ajout…',
    type: 'Type',
    types: {
      text: 'Texte',
      number: 'Nombre',
      date: 'Date',
      select: 'Liste',
      boolean: 'Oui / Non',
      email: 'E-mail',
      phone: 'Téléphone',
    },
    hints: {
      text: 'Texte libre, par exemple un nom d’entreprise.',
      number: 'Des nombres à comparer dans les conditions, par exemple un budget.',
      date: 'Une date, par exemple un renouvellement ou un anniversaire.',
      select: 'Une option parmi une liste que vous définissez.',
      boolean: 'Un simple oui ou non.',
      email: 'Une adresse e-mail, vérifiée à l’enregistrement.',
      phone: 'Un numéro de téléphone, vérifié à l’enregistrement.',
    },
    key: 'clé : {{key}}',
    keyNoLetters: 'La clé est faite des lettres et chiffres du nom : ajoutez-en.',
    keyLetterFirst: 'Une clé commence par une lettre : commencez le nom par un mot.',
    keyReserved: '« {{key}} » est un attribut de contact intégré : choisissez un autre nom.',
    options: 'Options',
    optionsHint: 'Une option par ligne.',
    optionsNeeded: 'Ajoutez au moins une option pour ajouter ce champ.',
    adminsOnly: 'Seuls les administrateurs peuvent modifier les champs personnalisés.',
    list: 'Champs personnalisés',
    archived: 'Archivé',
    edit: 'Modifier {{label}}',
    more: 'Plus d’actions pour {{label}}',
    archive: 'Archiver',
    restore: 'Restaurer',
    delete: 'Supprimer',
    emptyTitle: 'Aucun champ personnalisé',
    emptyBody: 'Stockez des données structurées sur vos contacts.',
    emptyStart: 'Commencez par un champ courant :',
    emptyAdmin: 'Un administrateur peut ajouter le premier.',
    suggestions: { company: 'Entreprise', budget: 'Budget', product: 'Produit d’intérêt' },
    suggest: 'Ajouter {{label}}',
    deleteTitle: 'Supprimer « {{label}} » ?',
    deleteBody: 'Le champ est supprimé définitivement, avec sa valeur sur chaque contact. Pour seulement le masquer des formulaires, archivez-le : un champ archivé garde ses données.',
    cancel: 'Annuler',
    deleting: 'Suppression…',
    confirmDelete: 'Supprimer le champ et ses données',
    editTitle: 'Modifier le champ',
    keyLabel: 'Clé',
    keyHint: 'Une clé est définitive : les variables et les imports y font référence.',
    editOptionsHint: 'Une par ligne. Une option encore utilisée sur un contact ne peut pas être retirée.',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
  },
  usage: {
    plan: 'Offre actuelle',
    title: 'Consommation',
    aside: 'Les limites qui bloquent d’abord',
    atLimit: 'Limite atteinte',
    overLimit: 'Limite dépassée',
    atLimitCount_one: '{{count}} limite au maximum',
    atLimitCount_other: '{{count}} limites au maximum',
    headroom: 'Marge disponible',
    unlimited: 'Illimité',
    meters: {
      contacts: 'Contacts',
      users: 'Utilisateurs',
      inboxSeats: 'Sièges Conversations',
      channels: 'Canaux connectés',
      automations: 'Automatisations',
      activeAutomations: 'Automatisations actives',
      customFields: 'Champs personnalisés',
      executions: 'Déclenchements ce mois-ci',
      messages: 'Messages ce mois-ci',
    },
    usersHint_one: '{{users}} actifs + {{count}} invitation en attente',
    usersHint_other: '{{users}} actifs + {{count}} invitations en attente',
  },
  billing: {
    due: 'à régler avant le {{date}}',
    overdue: 'en retard depuis le {{date}}',
    invoiceLine: 'Facture {{number}} · {{amount}}',
    howToPay: 'Comment payer',
    howToPayNote: 'Indiquez le numéro de facture avec votre paiement. Cette ligne disparaît dès que nous l’avons enregistré.',
    invoices: 'Factures',
    noInvoices: 'Aucune facture pour l’instant.',
    columns: { number: 'Facture', period: 'Période', due: 'Échéance', amount: 'Montant', status: 'Statut', pdf: 'PDF' },
    left: 'reste {{amount}}',
    paidOn: 'payée le {{date}}',
    status: { issued: 'À régler', overdue: 'En retard', paid: 'Payée', void: 'Annulée' },
    pdf: 'PDF',
    downloadPdf: 'Télécharger le PDF',
    downloading: 'Téléchargement…',
    pdfUnavailable: 'PDF indisponible — réessayer',
    pdfFor: '{{label}} : facture {{number}}',
    downloadFailed: 'Le téléchargement a échoué. Vérifiez votre connexion et réessayez.',
  },
  /** Paramètres › Mon offre (D-183): the plan, the paid plans to move to, the request. */
  myPlan: {
    interval: { month: 'par mois', quarter: 'par trimestre', year: 'par an' },
    /** « Offert » (D-183, 2026-10-31): the plan's name, no price, no renewal. */
    complimentaryName: '{{plan}} · Offert',
    status: {
      trialing: 'Essai jusqu’au {{date}}',
      active: 'Active · renouvellement le {{date}}',
      complimentary: 'Active',
      complimentaryUntil: 'Active · jusqu’au {{date}}',
      pastDue: 'Paiement en retard',
      suspended: 'Suspendue',
    },
    suspension: {
      trialTitle: 'Votre essai Pro est terminé',
      complimentaryTitle: 'Votre offre offerte est terminée',
      trialBody: 'Vos canaux, vos automatisations et vos conversations sont à l’arrêt. Choisissez une offre ci-dessous : votre espace reprend dès que votre paiement est confirmé.',
      otherTitle: 'Votre espace est suspendu',
      otherBody: 'Vos canaux, vos automatisations et vos conversations sont à l’arrêt. Votre offre et vos factures restent consultables ici.',
    },
    request: {
      pendingTitle: 'En attente de votre paiement',
      pendingBody: 'Vous avez demandé l’offre {{plan}} ({{price}}). Les instructions de paiement vous ont été envoyées par e-mail ; l’offre s’applique à réception du paiement.',
      paymentInstructions: 'Modalités de paiement',
      scheduledTitle: 'Passera à {{plan}} le {{date}}',
      scheduledBody: 'Votre offre actuelle reste en place jusqu’à cette date, sans remboursement.',
      cancel: 'Annuler la demande',
      cancelTitle: 'Annuler la demande ?',
      cancelBody: 'La demande d’offre {{plan}} sera retirée. Vous pourrez en faire une autre ensuite.',
      keep: 'Garder la demande',
      canceled: 'Demande annulée',
      cancelFailed: 'La demande n’a pas pu être annulée. Réessayez.',
    },
    compare: {
      title: 'Changer d’offre',
      billing: 'Facturation',
      intervals: { month: 'Mensuel', quarter: 'Trimestriel', year: 'Annuel' },
      current: 'Offre actuelle',
      highlighted: 'Offre sélectionnée',
      choose: 'Choisir cette offre',
      chooseLabel: 'Choisir l’offre {{plan}}',
      ownerOnly: 'Seul le propriétaire peut changer d’offre.',
      limit: '{{label}} : {{value}}',
      empty: 'Aucune offre n’est proposée pour le moment.',
    },
    confirm: {
      title: 'Passer à {{plan}}',
      price: '{{price}} {{interval}}',
      upgradeBody: 'Les instructions de paiement vous arriveront par e-mail. L’offre {{plan}} s’applique dès réception de votre paiement.',
      downgradeBody: 'Le changement prendra effet le {{date}}, à la fin de la période déjà payée, sans remboursement.',
      /** The unused part of the paid period pays the whole new plan (D-183, amended 2026-10-31). */
      creditCovers: 'Votre crédit restant de {{credit}} couvre {{plan}} jusqu’au {{date}}.',
      amountDue: 'Montant à payer : {{amount}}.',
      coveredBody: 'L’offre {{plan}} s’applique dès que notre équipe confirme votre demande.',
      /** The credit of a settled period pays it: applied at once, no confirmation (D-183, amended 2026-10-31). */
      coveredAtOnceBody: 'L’offre {{plan}} s’applique dès maintenant, sans attendre de paiement.',
      activate: 'Activer l’offre',
      send: 'Envoyer la demande',
      back: 'Retour',
      sentUpgrade: 'Demande envoyée',
      sentDowngrade: 'Changement programmé',
      pending: 'Une demande est déjà en cours pour cet espace. La page vient d’être mise à jour.',
      failed: 'La demande n’a pas pu être envoyée. Réessayez.',
    },
    /** A plan applied at once because the remaining credit pays it (D-183, amended 2026-10-31). */
    creditActive: 'Votre offre {{plan}} est active. Votre crédit restant couvre jusqu’au {{date}} : rien à payer.',
    usageSuspended: 'La consommation n’est pas affichée pendant la suspension. Elle revient dès que l’espace est réactivé.',
  },
  /** A feature the plan does not include (D-183): shown, never hidden. */
  planLock: {
    lockLabel: 'Verrouillé',
    title: {
      scheduledPosts: 'Publications et stories programmées.',
      campaigns: 'Campagnes.',
      segments: 'Segments.',
      httpRequestStep: 'Étape Requête HTTP.',
      apiAccess: 'Tokens d’API et serveur MCP.',
    },
    availableFrom: 'Disponible à partir de {{plan}}.',
    notIncluded: 'Non incluse dans votre offre.',
    askOwner: 'Non incluse dans votre offre. Demandez au propriétaire de l’espace.',
    seePlans: 'Voir les offres',
  },
  /** A write the plan refused (402, D-183), in the reader's language. */
  planBlock: {
    featureTitle: 'Non incluse dans votre offre',
    limitTitle: 'Limite de votre offre atteinte',
    feature: {
      scheduledPosts: 'Les publications et stories programmées ne sont pas incluses dans votre offre.',
      campaigns: 'Les campagnes ne sont pas incluses dans votre offre.',
      segments: 'Les segments ne sont pas inclus dans votre offre.',
      httpRequestStep: 'L’étape Requête HTTP n’est pas incluse dans votre offre.',
      apiAccess: 'Les tokens d’API et le serveur MCP ne sont pas inclus dans votre offre.',
      analyticsHistory: 'Votre offre conserve {{days}} jours de statistiques. Choisissez une période plus courte ou changez d’offre.',
      other: 'Cette fonctionnalité n’est pas incluse dans votre offre.',
    },
    limit: '{{label}} : la limite de votre offre ({{limit}}) est atteinte.',
    limitOther: 'Une limite de votre offre est atteinte.',
    change: 'Changer d’offre',
    askOwner: 'Demandez au propriétaire de l’espace.',
  },
  /** The screen every page but Mon offre shows while the workspace is suspended (D-183). */
  suspended: {
    trialTitle: 'Votre essai Pro est terminé',
    complimentaryTitle: 'Votre offre offerte est terminée',
    otherTitle: 'Cet espace est suspendu',
    body: 'Les canaux, les automatisations et les conversations sont à l’arrêt.',
    ownerBody: 'Choisissez une offre pour réactiver l’espace.',
    choose: 'Choisir une offre',
    agentBody: 'Contactez le propriétaire de l’espace.',
  },
  /** The page an empty gated list shows instead (D-183): what the plan opens, an example, the way to it by role. */
  upsell: {
    includedIn: 'Inclus dans {{plan}}',
    notIncludedPill: 'Non incluse dans votre offre',
    notOffered: 'Non incluse dans les offres proposées.',
    previewLabel: 'Aperçu · Exemple',
    availableWith: 'Disponible avec {{plan}}',
    alsoTitle: 'Le passage à {{plan}} débloque aussi',
    compare: 'Comparer toutes les offres →',
    seeMyPlan: 'Voir Mon offre',
    per: { month: '/mois', quarter: '/trimestre', year: '/an' },
    upgrade: 'Passer à {{plan}}',
    upgradeWithPrice: 'Passer à {{plan}} · {{price}}{{interval}}',
    context: {
      current: 'Vous êtes sur {{current}} ({{price}}{{interval}}).',
      currentPlain: 'Vous êtes sur {{current}}.',
      trial: 'Vous êtes en essai de {{current}} jusqu’au {{date}}.',
      activation: '{{plan}} est activé dès confirmation de votre paiement.',
      ownerOnly: 'Seul le propriétaire de l’espace peut changer d’offre.',
    },
    request: {
      pending: 'Demande de passage à {{plan}} envoyée — en attente de votre paiement',
      scheduled: 'Changement vers {{plan}} prévu le {{date}}',
    },
    ask: {
      action: 'Demander au propriétaire',
      sent: 'Demande envoyée au propriétaire',
      alreadySent: 'Déjà demandé le {{date}}',
      noOwner: 'Aucun propriétaire actif à prévenir pour cet espace.',
      failed: 'La demande n’a pas pu être envoyée. Réessayez.',
    },
    bar: {
      label: 'Passer à l’offre {{plan}}',
      path: '{{current}} → {{plan}}',
    },
    unlocks: {
      scheduledPosts: { title: 'Publications et stories programmées', body: 'Préparez vos posts et vos stories à l’avance : ils partent à l’heure prévue.' },
      campaigns: { title: 'Campagnes', body: 'Lancez une série de publications en une fois, après l’avoir vérifiée.' },
      segments: { title: 'Segments', body: 'Relancez seulement les personnes qui ont demandé le prix sans commander.' },
      httpRequestStep: { title: 'Requête HTTP dans les flux', body: 'Envoyez les commandes reçues en DM vers votre boutique ou votre tableur.' },
      apiAccess: { title: 'Tokens d’API et serveur MCP', body: 'Connectez votre assistant IA pour lire vos statistiques et préparer des brouillons d’automatisation.' },
    },
    limits: {
      users: '{{users}} utilisateurs',
      usersUnlimited: 'Utilisateurs illimités',
      days: '{{days}} jours de stats',
      daysUnlimited: 'Historique illimité',
      body: 'Toute l’équipe sur la boîte de réception, et {{days}} jours d’historique.',
      bodyUnlimited: 'Toute l’équipe sur la boîte de réception, et tout l’historique.',
    },
    days: { mon: 'Lun', tue: 'Mar', wed: 'Mer', thu: 'Jeu', fri: 'Ven', sun: 'Dim' },
    speakers: { you: 'Vous', assistant: 'Claude' },
    tags: { post: 'Post', story: 'Story', photo: 'Photo', carousel: 'Carrousel', reel: 'Reel', video: 'Vidéo', read: 'Lecture', write: 'Écriture' },
    variants: {
      campaigns: {
        title: 'Préparez une semaine de publications en une seule fois.',
        body: 'Fini les posts publiés un par un à la dernière minute. Composez votre lancement, vérifiez le calendrier d’un coup d’œil, puis laissez Repliq publier à l’heure prévue.',
        benefit1: 'Posts et stories programmés ensemble, sur Instagram et Facebook',
        benefit2: 'Rien ne part avant votre validation : vous lancez quand tout est prêt',
        benefit3: 'Idéal pour un lancement de collection, des soldes ou un code promo',
        previewTitle: 'Nouvelle collection — semaine de lancement',
        previewMeta: '5 publications · lundi → dimanche · Instagram',
        previewBadge: 'Prête à lancer',
        action: 'Lancer la campagne',
        more: '+ 2 autres publications',
        rows: {
          r1: 'Teaser : « Quelque chose arrive… »',
          r2: 'Révélation de la collection',
          r3: 'Carrousel : 6 looks portés',
          r4: 'Code promo réservé aux abonnées',
          r5: 'Dernières pièces en stock',
        },
      },
      posts: {
        title: 'Programmez vos publications, Repliq les publie pour vous.',
        body: 'Rédigez vos posts quand vous avez le temps, choisissez le jour et l’heure : Repliq les publie à l’heure prévue sur Instagram et Facebook, sans que vous ayez à y penser.',
        benefit1: 'Photos, carrousels et Reels programmés au jour et à l’heure choisis',
        benefit2: 'Une copie sur votre Page Facebook, en même temps que sur Instagram',
        benefit3: 'Les statistiques de chaque publication une fois en ligne : j’aime, commentaires, portée',
        previewTitle: 'Semaine de la rentrée',
        previewMeta: '5 publications · lundi → vendredi · Instagram',
        previewBadge: 'À venir',
        action: 'Programmer la publication',
        more: '+ 2 autres publications',
        rows: {
          r1: 'Les nouveautés de la semaine',
          r2: 'Dans les coulisses de l’atelier',
          r3: '5 façons de le porter',
          r4: 'L’avis d’une cliente',
          r5: 'Le récap de la semaine',
        },
      },
      stories: {
        title: 'Gardez vos stories actives toute la semaine.',
        body: 'Une story ne reste en ligne que 24 heures. Préparez toute la suite en une fois : Repliq publie chacune à l’heure prévue, sur Instagram et Facebook.',
        benefit1: 'Une photo ou une vidéo par story, publiée à l’heure choisie',
        benefit2: 'Toute la semaine préparée en une fois, publiée jour après jour',
        benefit3: 'Vues, portée, réponses et partages de chaque story pendant ses 24 heures',
        previewTitle: 'Compte à rebours des soldes',
        previewMeta: '5 stories · lundi → vendredi · Instagram',
        previewBadge: 'À venir',
        action: 'Programmer la story',
        more: '+ 2 autres stories',
        rows: {
          r1: 'J-4 : les soldes approchent',
          r2: 'Les coulisses de la préparation',
          r3: 'Aperçu des pièces soldées',
          r4: 'Demain, c’est le grand jour',
          r5: 'C’est parti : jusqu’à -30 %',
        },
      },
      developers: {
        title: 'Pilotez Repliq depuis votre assistant IA.',
        body: 'Créez un token d’API et connectez Claude à votre espace par le protocole MCP. Demandez-lui vos statistiques, ou de préparer une automatisation que vous relirez avant de la publier.',
        benefit1: 'Vos canaux, vos automatisations et leurs statistiques, lus par votre assistant',
        benefit2: 'Des brouillons d’automatisation préparés pour vous, publiés seulement après votre relecture',
        benefit3: 'Des tokens à durée limitée, révocables à tout moment',
        previewTitle: 'Claude — connecté par MCP',
        previewMeta: 'Token rpq_••••••••3f9a · Lecture et écriture',
        previewBadge: 'Actif',
        action: 'Créer un token',
        more: '+ 2 autres échanges',
        rows: {
          r1: 'Combien de déclenchements cette semaine ?',
          r2: '1 284 déclenchements, dont 312 pour « Prix en DM ».',
          r3: 'Prépare une réponse automatique aux commentaires « prix ».',
          r4: 'Brouillon « Réponse prix » créé : à relire avant publication.',
          r5: 'Quels canaux sont connectés ?',
        },
      },
    },
  },
  segments: {
    namePlaceholder: 'ex. VIP qui nous ont écrit ce mois-ci',
    new: 'Nouveau segment',
    edit: 'Modifier le segment',
    name: 'Nom',
    nameNeeded: 'Donnez un nom au segment.',
    matching: 'Contacts qui remplissent',
    match: 'Correspondance',
    matchAll: 'toutes',
    matchAny: 'au moins une',
    ofConditions: 'de ces conditions',
    types: { tag: 'Tag', field: 'Champ personnalisé', channel: 'Canal', status: 'Statut', created: 'Ajouté', last_interaction: 'Dernière interaction' },
    part: {
      type: 'Condition {{n}} : type',
      operator: 'Condition {{n}} : opérateur',
      tag: 'Condition {{n}} : tag',
      field: 'Condition {{n}} : champ',
      value: 'Condition {{n}} : valeur',
      channel: 'Condition {{n}} : canal',
      status: 'Condition {{n}} : statut',
      days: 'Condition {{n}} : jours',
    },
    ops: {
      is: 'est',
      isNot: 'n’est pas',
      chooseTag: 'Choisir un tag…',
      chooseField: 'Choisir un champ…',
      isSet: 'a une valeur',
      isNotSet: 'est vide',
      eq: 'est',
      gt: 'est supérieur à',
      lt: 'est inférieur à',
      contains: 'contient',
      within: 'dans les derniers',
      older: 'il y a plus de',
      never: 'jamais',
      days: 'jours',
      daysAgo: 'jours',
    },
    remove: 'Retirer la condition {{n}}',
    add: '+ Condition',
    finish: 'Complétez les conditions pour voir qui correspond.',
    count_one: '{{count}} contact correspond',
    count_other: '{{count}} contacts correspondent',
    counting: 'Calcul…',
    cancel: 'Annuler',
    save: 'Enregistrer le segment',
    create: 'Créer le segment',
  },
  composerFields: {
    captionPlaceholder: 'ex. Nouveauté en boutique ! Commentez PRIX pour recevoir le lien. Mentionnez avec @identifiant.',
    hashtagPlaceholder: 'ex. #promo',
    collaboratorPlaceholder: 'ex. @marque_partenaire',
    usernamePlaceholder: 'ex. @nom_utilisateur',
  },
  /** A comment trigger pinned to a scheduled post (PostPinPicker); the rest migrates with the builder (Phase 8). */
  postPin: {
    cancelled: 'La publication a été annulée : programmez-la de nouveau ou choisissez-en une autre, sinon ce déclencheur ne s’activera jamais.',
  },
  /** The composer (R16, R19, R24, R26): one for posts and stories. */
  composer: {
    summary: {
      plannedFor: 'Prévue le {{when}}',
      now: 'Publication dès votre confirmation',
      pickTime: 'Choisissez une date et une heure',
    },
    toast: {
      scheduledFor: 'Programmée pour le {{when}}',
      plannedFor: 'Prévue le {{when}}',
      draftSaved: 'Brouillon enregistré',
      publishingNow: 'Publication en cours',
      saveFailed: 'Impossible d’enregistrer le brouillon',
    },
    title: {
      post: { new: 'Nouvelle publication', edit: 'Modifier la publication', scheduled: 'Publication programmée' },
      story: { new: 'Nouvelle story', edit: 'Modifier la story', scheduled: 'Story programmée' },
    },
    unsaved: 'Non enregistrée',
    back: { post: 'Publications', story: 'Stories' },
    view: { label: 'Affichage', edit: 'Modifier', preview: 'Aperçu' },
    account: 'Compte',
    accountOption: '{{name}} (publication impossible)',
    openChannels: 'Ouvrir Canaux',
    goToChannels: 'Aller dans Canaux',
    noAccount: 'Connectez d’abord un compte Instagram ou une Page Facebook.',
    format: 'Format',
    caption: 'Légende',
    /** The write column's one card (Murmura M2, D-187). */
    content: 'Contenu',
    counters: {
      characters: '{{value}} / {{max}} caractères',
      hashtags: '{{value}} / {{max}} hashtags',
      mentions: '{{value}} / {{max}} mentions',
    },
    hashtags: 'Hashtags',
    hashtagsHint: 'Ajoutés à la fin de la légende.',
    hashtagInvalid: '#{{tag}} n’est pas un hashtag valide : lettres, chiffres et tirets bas uniquement.',
    collaborators: 'Collaborateurs',
    collaboratorsMax: '· {{max}} comptes max',
    collaboratorsHint: 'Ils sont invités à la partager sur leur profil.',
    collaboratorInvalid: '@{{name}} n’est pas un nom d’utilisateur Instagram valide.',
    shareToFeed: 'Afficher aussi le reel dans le fil principal',
    campaign: {
      partOf: 'Fait partie de la campagne',
      holds: {
        post: 'La publication est enregistrée avec son horaire prévu et part au lancement de la campagne.',
        story: 'La story est enregistrée avec son horaire prévu et part au lancement de la campagne.',
      },
      live: {
        post: 'La campagne est active : la publication est programmée dès que vous l’enregistrez.',
        story: 'La campagne est active : la story est programmée dès que vous l’enregistrez.',
      },
    },
    when: {
      label: 'Quand',
      schedule: 'Programmer',
      now: 'Publier maintenant',
      date: 'Date',
      time: 'Heure',
      pickDate: 'Choisir une date',
      pickTime: 'Choisir une heure',
      holds: 'L’horaire prévu : la publication part à ce moment-là, une fois la campagne lancée.',
      /** Under the date and time pills (Murmura M2): the zone the times are in is the workspace's (D-146). */
      zoneSentence: 'Heures du fuseau de l’espace, {{zone}}. Elle part à cette heure-là quel que soit l’endroit où vous êtes.',
      /** Under the large date: `formatCountdown`'s span, "143 jours". */
      leavesIn: 'Part dans {{span}}',
    },
    /** In "Quand" (Murmura M2, D-187): the other items scheduled the same day in the workspace zone, only the parts that are not zero. */
    sameDay: {
      none: 'Ce jour-là : aucune autre publication ni story programmée.',
      label: 'Ce jour-là : {{list}}.',
      posts_one: '{{count}} autre publication',
      posts_other: '{{count}} autres publications',
      stories_one: '{{count}} autre story',
      stories_other: '{{count}} autres stories',
    },
    /** The header stats (Q19, D-187): this kind's tabs Brouillons and À venir. */
    stats: {
      drafts_one: 'Brouillon',
      drafts_other: 'Brouillons',
      scheduled_one: 'Programmée',
      scheduled_other: 'Programmées',
    },
    /** The dark action card (Murmura M2): its name, and the line shown while the primary action can go. */
    actions: 'Enregistrer',
    ready: {
      title: 'Prête à partir',
      detail: '{{channels}} · {{when}}',
      withPage: '{{platform}} + {{page}}',
      now: 'dès votre confirmation',
    },
    cta: {
      post: 'Programmer la publication',
      story: 'Programmer la story',
      saveChanges: 'Enregistrer les modifications',
      publishNow: 'Publier maintenant',
      saveToCampaign: 'Enregistrer dans la campagne',
      saving: 'Enregistrement…',
    },
    secondary: {
      draft: 'Enregistrer en brouillon',
      unschedule: 'Déprogrammer (repasser en brouillon)',
      noTime: 'Enregistrer sans horaire',
    },
    problems: 'Problèmes à corriger',
    need: {
      image: 'Ajoutez une photo',
      carousel: 'Ajoutez des photos ou des vidéos',
      reel: 'Ajoutez une vidéo',
      story: 'Ajoutez une photo ou une vidéo',
    },
    gate: {
      pickAccount: 'Choisissez le compte qui publie.',
      upload: 'Attendez la fin de l’envoi.',
      campaign: 'Chargement de la campagne…',
      post: {
        schedule: '{{need}} pour programmer cette publication.',
        publish: '{{need}} pour publier cette publication.',
        plan: '{{need}} pour prévoir cette publication.',
      },
      story: {
        schedule: '{{need}} pour programmer cette story.',
        publish: '{{need}} pour publier cette story.',
        plan: '{{need}} pour prévoir cette story.',
      },
    },
    unsavable: {
      loading: 'Chargement',
      pickAccount: 'Choisissez un compte',
      connect: 'Connectez un compte Instagram ou une Page Facebook',
      upload: 'Envoi en cours',
      campaign: 'Chargement de la campagne',
    },
    note: {
      applySchedule: 'Les modifications s’appliquent quand vous programmez',
      applyPublish: 'Les modifications s’appliquent quand vous publiez',
      applySave: 'Les modifications s’appliquent quand vous les enregistrez',
      notSaved: 'Pas encore enregistrée · {{reason}}',
    },
    blockers: {
      account: 'Ce compte ne peut pas publier.',
      page: 'Cette Page ne peut pas publier.',
      pageReason: '{{page}} : {{reason}}',
      canceledCampaign: 'Cette campagne est arrêtée. Ses publications ne peuvent plus être modifiées.',
      onCopy: 'Sur {{page}} : {{issue}}',
    },
    schedule: {
      pickDateTime: 'Choisissez une date et une heure.',
      future: 'Choisissez un horaire à venir.',
      futureOrNow: 'Choisissez un horaire à venir, ou Publier maintenant.',
    },
    nothingToSave: 'Rien à enregistrer',
    preview: {
      label: 'Aperçu',
      story: 'Aperçu de la story',
      media: 'Aperçu du média',
      caption: 'Votre légende apparaît ici.',
      more: 'plus',
      sendMessage: 'Envoyer un message',
      previous: 'Élément précédent',
      next: 'Élément suivant',
      account: 'votre.compte',
      on: 'Aperçu sur',
    },
  },
  /** The composer's Média card (MediaPanel, prepareMedia, TagPeopleEditor). */
  composerMedia: {
    drop: {
      image: {
        empty: 'Déposez une photo ou cliquez pour l’ajouter',
        more: 'Déposez une autre photo pour la remplacer',
        hint: 'JPEG, PNG ou WebP · convertie et recadrée entre 4:5 et 1,91:1',
        hintPage: 'JPEG, PNG ou WebP · 4 Mo max',
        hintBoth: 'JPEG, PNG ou WebP · 4 Mo max · recadrée entre 4:5 et 1,91:1',
      },
      carousel: {
        empty: 'Déposez des photos ou cliquez pour les ajouter',
        more: 'Déposez d’autres photos ou vidéos, ou cliquez pour les ajouter',
        morePage: 'Déposez d’autres photos, ou cliquez pour les ajouter',
        hint: '2 à 10 photos ou vidéos · vidéos de 60 s max · photos recadrées entre 4:5 et 1,91:1',
        hintPage: '2 à 10 photos — Facebook n’accepte pas de vidéo ici',
        hintBoth: '2 à 10 photos — Facebook n’accepte pas de vidéo ici · recadrées entre 4:5 et 1,91:1',
      },
      reel: {
        empty: 'Déposez une vidéo ou cliquez pour l’ajouter',
        more: 'Déposez une autre vidéo pour la remplacer',
        hint: 'MP4 ou MOV, de 3 s à 15 min, 300 Mo max · 9:16 recommandé',
        hintPage: 'MP4 ou MOV, de 3 à 90 s · 9:16 recommandé',
      },
      story: {
        empty: 'Déposez une photo ou une vidéo, ou cliquez pour l’ajouter',
        more: 'Déposez une autre photo ou vidéo pour la remplacer',
        hint: 'JPEG, PNG ou WebP, ou MP4/MOV de 3 à 60 s · 100 Mo max · affichée telle quelle, 9:16 recommandé',
      },
    },
    chooseFile: 'Choisir un fichier',
    addMedia: 'Ajouter un média',
    list: 'Médias',
    tag: 'Identifier des personnes',
    tagShort: 'Identifier',
    alt: 'Texte alternatif',
    altShort: 'Texte alt.',
    describe: 'Décrire la photo',
    remove: 'Retirer',
    moveLeft: 'Déplacer l’élément {{n}} vers la gauche',
    moveRight: 'Déplacer l’élément {{n}} vers la droite',
    deleteItem: 'Supprimer l’élément {{n}}',
    tagged_one: '{{count}} identifiée',
    tagged_other: '{{count}} identifiées',
    dismiss: 'Ignorer',
    uploading: 'Envoi de {{name}}',
    full: 'Vous en avez {{max}} : le maximum pour un carrousel.',
    note: '{{name}} : {{note}}',
    refused: {
      image: 'ce n’est pas une photo, il a été ignoré.',
      carousel: 'ce n’est ni une photo ni une vidéo MP4/MOV, il a été ignoré.',
      reel: 'ce n’est pas une vidéo MP4 ou MOV, il a été ignoré.',
      story: 'ce n’est ni une photo ni une vidéo MP4/MOV, il a été ignoré.',
    },
    skipped_one: '{{count}} fichier ignoré : un carrousel contient {{max}} éléments.',
    skipped_other: '{{count}} fichiers ignorés : un carrousel contient {{max}} éléments.',
    onlyOne: 'Seul {{name}} a été utilisé : ce format prend un seul fichier.',
    cover: {
      title: 'Image de couverture',
      hint: 'Facultative. Sans couverture, Instagram utilise la première image.',
      hintBoth: 'Facultative, Instagram uniquement. Sans couverture, Instagram utilise la première image ; Facebook choisit toujours la sienne.',
      change: 'Changer',
      add: 'Ajouter une couverture',
      input: 'Ajouter une image de couverture',
    },
    modal: {
      tag: 'Identifier des personnes et décrire la photo',
      describe: 'Décrire la photo',
      altHint: 'Décrit la photo pour les personnes qui utilisent un lecteur d’écran. Facultatif.',
      done: 'Terminé',
    },
    prepare: {
      unknown: 'Utilisez une photo (JPEG, PNG, WebP) ou une vidéo (MP4, MOV).',
      coverPhoto: 'La couverture doit être une photo.',
      videoType: 'Utilisez une vidéo MP4 ou MOV.',
      storySize: 'Une vidéo de story fait au plus 100 Mo.',
      videoSize: 'Les vidéos font au plus 300 Mo.',
      reelPage: 'Un reel pour Facebook dure entre 3 et 90 secondes.',
      reel: 'Un reel dure entre 3 secondes et 15 minutes.',
      storyLength: 'Une vidéo de story dure entre 3 et 60 secondes.',
      carouselLength: 'Une vidéo de carrousel dure entre 3 et 60 secondes.',
      noCanvas: 'Ce navigateur ne peut pas préparer la photo.',
      tooLarge: 'Cette photo est trop lourde, même après compression.',
      cannotOpen: 'Ce format de photo ne s’ouvre pas dans ce navigateur.',
      croppedPortrait: 'Recadrée en portrait 4:5',
      croppedLandscape: 'Recadrée en paysage 1,91:1',
      converted: 'Convertie en JPEG',
      resized: 'Redimensionnée',
    },
    tagPeople: {
      photo: 'Photo : cliquez là où se trouve la personne pour l’identifier',
      removeTag: 'Retirer l’identification @{{name}}',
      username: 'Nom d’utilisateur à identifier',
      invalid: 'Saisissez un nom d’utilisateur Instagram : lettres, chiffres, points et tirets bas.',
      already: '@{{name}} est déjà identifié sur cette photo.',
      submit: 'Identifier',
      cancel: 'Annuler',
      full: '{{max}} personnes : le maximum qu’Instagram autorise sur une photo.',
      help: 'Cliquez sur la photo là où se trouve la personne. Les personnes identifiées sont prévenues à la mise en ligne de la publication.',
    },
    chips: {
      remove: 'Retirer {{value}}',
      max: '{{max}} au maximum.',
    },
  },
  /** "Publier aussi sur Facebook" (D-143). */
  crossPost: {
    title: 'Publier aussi sur Facebook',
    post: 'La même publication part sur votre Page au même moment. Les collaborateurs, les personnes identifiées et la couverture du reel restent sur Instagram.',
    story: 'La même story est publiée sur votre Page au même moment.',
    page: 'Page',
    lastTry: 'Dernier essai sur {{page}} : {{error}}',
    alsoOn: 'Aussi sur Facebook',
    publishing: 'En cours de publication sur {{page}}. Elle reste telle quelle : les modifications faites ici ne lui sont pas reportées.',
    published: 'Déjà publiée sur {{page}}. Elle reste telle quelle : les modifications faites ici ne lui sont pas reportées.',
    connect: {
      before: 'Vous la voulez aussi sur Facebook ?',
      link: 'Connectez une Page Facebook',
      post: 'et la même publication pourra y partir au même moment.',
      story: 'et la même story pourra y partir au même moment.',
    },
  },
  /** The composer's "Automatisations sur cette publication" card (D-103, D-104). */
  postAutomations: {
    title: 'Automatisations sur cette publication',
    lede: 'Elles répondent aux commentaires de cette publication uniquement et s’activent à sa mise en ligne.',
    quick: {
      auto_dm: { title: 'Commentaire → DM', body: 'Envoyer un lien en DM à chaque personne qui commente' },
      reply: { title: 'Répondre au commentaire', body: 'Remerciement public sous chaque commentaire' },
    },
    linked: {
      live: 'Active',
      goesLive: 'S’active à la mise en ligne',
      archived: 'Archivée',
    },
    open: 'Ouvrir',
    created: 'Créées quand vous programmez la publication ou l’enregistrez en brouillon.',
    createdKept: 'Créées quand vous programmez la publication ou l’enregistrez en brouillon. D’ici là, elles sont gardées dans ce navigateur.',
    add: 'Ajouter une automatisation',
    /** The folded card's summary (Murmura M2, D-187). */
    fold: {
      linked_one: '{{count}} liée',
      linked_other: '{{count}} liées',
      toCreate: '{{count}} à créer',
      none: 'Aucune',
    },
    summary: {
      specific: 'Commentaires avec {{words}}',
      every: 'Chaque commentaire',
      publicReply: 'réponse publique',
      followers: 'abonnés uniquement',
      dmLink: 'DM avec un lien',
      dm: 'DM',
      tag: 'tag « {{name}} »',
    },
    template: {
      label: 'Enregistrer comme modèle pour vos prochaines publications',
      replaces: 'Remplace votre modèle « {{name}} ».',
      failed: 'Impossible d’enregistrer le modèle.',
      cancel: 'Annuler',
      saving: 'Enregistrement…',
      replace: 'Remplacer le modèle',
      save: 'Enregistrer le modèle',
      saved: 'Enregistrée comme modèle « {{name}} ».',
    },
    card: {
      on: 'Activée avec la publication.',
      finishFirst: 'Terminez d’abord l’automatisation',
      saveAsTemplate: 'Enregistrer comme modèle',
      collapse: 'Réduire',
      edit: 'Modifier',
      remove: 'Retirer',
      removeLabel: 'Retirer {{name}}',
      when: 'Quand le commentaire contient',
      specific: 'un ou plusieurs mots précis',
      any: 'n’importe quel mot',
      then: 'Alors',
      publicReply: 'répondre publiquement sous le commentaire',
      publicReplyHint: 'Visible par tout le monde.',
      publicReplyField: 'Réponse publique',
      followersOnly: 'seuls les abonnés reçoivent le DM',
      followersHint: 'Instagram ne nous dit qui vous suit qu’après un appui sur un bouton de votre DM : ils reçoivent donc d’abord un message avec un bouton.',
      opener: 'Premier message, avec un bouton',
      buttonLabel: 'Libellé du bouton',
      followersNote: 'Quand ils appuient, les abonnés reçoivent le DM ci-dessous. Les autres reçoivent ce message, avec un bouton « {{button}} » pour vérifier à nouveau :',
      askField: 'Message pour les personnes qui ne vous suivent pas',
      sendFollowersDm: 'envoyer un DM aux abonnés',
      sendDm: 'envoyer un DM',
      dmHint: 'Avec votre lien, si vous en ajoutez un.',
      tag: 'leur ajouter un tag',
      tagHintDm: 'Ajouté à chaque personne qui reçoit le DM.',
      tagHintAll: 'Ajouté à chaque personne qui commente.',
      tagField: 'Nom du tag',
      tagPlaceholder: 'ex. Participant au concours',
    },
    picker: {
      title: 'Ajouter une automatisation à cette publication',
      lede: 'Vous la configurez sur la publication ; elle répond aux commentaires de cette publication uniquement, dès sa mise en ligne.',
      mine: 'Mes modèles',
      loading: 'Chargement de vos modèles…',
      noneBefore: 'Aucun pour l’instant. Configurez une automatisation sur une publication, puis utilisez',
      noneAfter: 'pour la réutiliser ici.',
      readyMade: 'Prêtes à l’emploi',
      custom: 'Personnalisée',
      scratch: 'Partir de zéro',
      scratchBody: 'Choisissez vous-même les étapes : réponse publique, abonnés uniquement, DM, tag.',
      add: 'Ajouter',
      delete: 'Supprimer',
      sure: 'Sûr ?',
      deleteLabel: 'Supprimer {{name}}',
      confirmDelete: 'Confirmer la suppression de {{name}}',
    },
    presets: {
      auto_dm: { label: 'DM automatique avec un lien', description: 'Chaque personne qui commente reçoit votre lien en DM.' },
      reply: { label: 'Répondre aux commentaires', description: 'Un remerciement public sous chaque commentaire.' },
      giveaway: {
        label: 'Organiser un concours',
        description: 'Un « regardez vos DM » public ; les abonnés sont inscrits et tagués, les autres sont invités à s’abonner.',
      },
      follow_gate: {
        label: 'Gagner des abonnés grâce aux commentaires',
        description: 'Les abonnés reçoivent le lien ; les autres sont d’abord invités à s’abonner.',
      },
      dm_tag: { label: 'Commentaire → DM + tag', description: 'Un DM à chaque personne qui commente, taguée pour pouvoir la recontacter.' },
      custom: { label: 'Automatisation personnalisée', description: 'Partez de zéro et choisissez vous-même les étapes.' },
    },
    problems: {
      replyOrDm: 'Activez une réponse publique ou un DM.',
      followersNeedDm: '« Abonnés uniquement » a besoin d’un DM pour les abonnés.',
      tagName: 'Nommez le tag, ou désactivez-le.',
      tagLength: 'Gardez le nom du tag sous {{max}} caractères.',
      opener: 'Rédigez le premier message, celui avec le bouton.',
      openerLength: 'Gardez le premier message sous {{max}} caractères.',
      buttonLabel: 'Donnez un libellé au bouton du premier message.',
      buttonLength: 'Gardez le libellé du bouton sous {{max}} caractères.',
      prefixed: '{{name}} : {{problem}}',
    },
    outcome: {
      onNow_one: '{{count}} automatisation est activée : elle se déclenche dès que la publication est en ligne.',
      onNow_other: '{{count}} automatisations sont activées : elles se déclenchent dès que la publication est en ligne.',
      onLater_one: '{{count}} automatisation est activée : elle se déclenche dès la mise en ligne de la publication.',
      onLater_other: '{{count}} automatisations sont activées : elles se déclenchent dès la mise en ligne de la publication.',
      notOn: '« {{name}} » a été créée mais n’est pas activée : {{reason}}.',
      failed: '« {{name}} » n’a pas pu être créée : {{reason}}.',
      retry: 'La publication est enregistrée ; ce qui n’a pas pu être créé reste sur sa page de modification : enregistrez à nouveau pour réessayer.',
      issues_one: 'elle a {{count}} problème à corriger dans l’éditeur visuel',
      issues_other: 'elle a {{count}} problèmes à corriger dans l’éditeur visuel',
      empty: 'elle est vide ({{reason}}) ; complétez-la dans l’éditeur visuel ou supprimez-la',
      unknown: 'Une erreur est survenue',
    },
  },
  /** What Instagram or Facebook would refuse in the composed post (limits.ts). */
  postIssues: {
    addReel: 'Ajoutez une vidéo pour le reel.',
    addStory: 'Ajoutez une photo ou une vidéo pour la story.',
    addPhoto: 'Ajoutez une photo.',
    addMedia: 'Ajoutez une photo ou une vidéo.',
    imageOne: 'Une publication image contient exactement une image.',
    reelOne: 'Un reel contient exactement une vidéo.',
    storyOne: 'Une story contient exactement une photo ou une vidéo.',
    multiPhotoCount: 'Une publication multi-photos contient entre {{min}} et {{max}} photos.',
    multiPhotoOnly: 'Sur Facebook, une publication multi-photos ne contient que des photos.',
    carouselCount: 'Un carrousel contient entre {{min}} et {{max}} photos ou vidéos.',
    storySize: 'Une vidéo de story fait au plus 100 Mo.',
    item: 'Élément {{n}}',
    thePhoto: 'La photo',
    pagePhotoSize: '{{label}} : Facebook accepte les photos jusqu’à 4 Mo.',
    tagsInstagramOnly: '{{label}} : on ne peut identifier des personnes que sur Instagram.',
    tagsPhotosOnly: '{{label}} : on ne peut identifier des personnes que sur des photos.',
    ratio: '{{label}} : Instagram demande des photos entre 4:5 (portrait) et 1,91:1 (paysage).',
    captionOver: 'La légende dépasse {{max}} caractères.',
    collaboratorsInstagram: 'Les collaborateurs sont une fonctionnalité Instagram.',
    hashtags: 'Utilisez au plus {{max}} hashtags.',
    mentions: 'Mentionnez au plus {{max}} comptes.',
    collaborators: 'Invitez au plus {{max}} collaborateurs.',
  },
  /** A published post or story, read-only (R17, R25), and a Facebook copy that follows its original (R18). */
  published: {
    title: {
      post: 'Publication publiée',
      story: 'Story publiée',
      publishingPost: 'Publication en cours de publication',
      publishingStory: 'Story en cours de publication',
    },
    note: {
      post: 'Cette publication est en ligne : elle ne peut plus être modifiée ici. Voici ce qui est parti, où, et comment elle performe.',
      story: 'Cette story est publiée : elle ne peut plus être modifiée. Voici ce qui est parti et comment elle performe.',
      publishingPost: 'Cette publication est en cours de publication : elle ne peut plus être modifiée.',
      publishingStory: 'Cette story est en cours de publication : elle ne peut plus être modifiée.',
    },
    viewOn: 'Voir sur {{platform}}',
    back: { post: 'Retour aux publications', story: 'Retour aux stories' },
    preview: 'Aperçu',
    stats: 'Statistiques',
    synced: 'Synchronisées {{ago}}',
    pending: 'Les chiffres d’Instagram arrivent sous 48 heures.',
    metric: {
      views: 'Vues',
      reach: 'Portée',
      likes: 'J’aime',
      comments: 'Commentaires',
      saved: 'Enregistrements',
      replies: 'Réponses',
      shares: 'Partages',
    },
    details: 'Détails',
    detail: {
      format: 'Format',
      publishedOn: 'Publiée le',
      alsoOn: 'Aussi sur',
      automations: 'Automatisations',
      none: 'Aucune',
      account: 'Compte',
      alsoOnFacebook: 'Aussi sur Facebook',
      yes: 'Oui · {{page}}',
      no: 'Non',
      original: 'Original',
      campaign: 'Campagne',
    },
    copy: {
      notePost: 'Cette publication est la copie Facebook (Page {{page}}) d’une publication de {{original}}. Elle suit l’original : modifiez l’original, la copie se met à jour.',
      noteStory: 'Cette story est la copie Facebook (Page {{page}}) d’une story de {{original}}. Elle suit l’original : modifiez l’original, la copie se met à jour.',
      editOriginal: 'Modifier l’original ({{account}})',
      noTime: 'pas encore d’horaire',
      page: 'Page {{name}} (Facebook)',
      original: '{{account}} · {{platform}}',
    },
  },
  /** The date range above every set of figures (Tableau de bord R01, Statistiques R29 and R30). */
  period: {
    label: 'Période',
    today: 'Aujourd’hui',
    d7: '7 jours',
    d30: '30 jours',
    custom: 'Personnalisé',
    heading: {
      today: 'Aujourd’hui',
      d7: 'Sur les 7 derniers jours',
      d30: 'Sur les 30 derniers jours',
      custom: 'Sur la période choisie',
    },
    from: 'Date de début',
    to: 'Date de fin',
    between: 'au',
    /** While a custom period is incomplete the figures stay on the previous one, as the heading says. */
    problem: {
      pickTwo: 'Choisissez deux dates. En attendant, les chiffres restent ceux de la période précédente.',
      order: 'La date de début doit précéder la date de fin.',
      tooLong: 'Choisissez une période de 400 jours au plus.',
    },
    /** Under a custom period: how far back the plan reads (D-183). */
    historyHint: 'Votre offre conserve {{days}} jours de statistiques.',
    versus: {
      today: 'par rapport à hier',
      d7: 'par rapport aux 7 jours précédents',
      d30: 'par rapport aux 30 jours précédents',
      custom: 'par rapport à la période précédente',
    },
    same: {
      today: 'identique à hier',
      d7: 'identique aux 7 jours précédents',
      d30: 'identique aux 30 jours précédents',
      custom: 'identique à la période précédente',
    },
  },
  /** Tableau de bord (R01, `Dashboard.dc.html`). */
  dashboard: {
    greeting: {
      morning: 'Bonjour',
      afternoon: 'Bonjour',
      evening: 'Bonsoir',
    },
    greetingNamed: '{{greeting}}, {{name}}',
    lede: 'Voici ce qui tourne dans {{workspace}}.',
    ledeSetup: 'Mettons votre espace de travail en ligne.',
    /** The lede once the figures are in: does everything work? (critique 2026-10-29) */
    verdict: {
      failing_one: '{{count}} automatisation a échoué sur la période : elle est en tête de la liste ci-dessous.',
      failing_other: '{{count}} automatisations ont échoué sur la période : elles sont en tête de la liste ci-dessous.',
      silent_one: 'Aucun échec, mais « {{name}} » est active et ne s’est pas déclenchée depuis 30 jours.',
      silent_other: 'Aucun échec, mais {{count}} automatisations actives ne se sont pas déclenchées depuis 30 jours.',
      allGood_one: 'Tout fonctionne : {{count}} déclenchement sur la période, sans échec.',
      allGood_other: 'Tout fonctionne : {{count}} déclenchements sur la période, sans échec.',
      quiet: 'Rien ne s’est déclenché sur la période.',
    },
    latestFailure: {
      named: 'Dernier échec {{when}} : « {{name}} ».',
      deleted: 'Dernier échec {{when}}, sur une automatisation supprimée depuis.',
      see: 'Voir',
    },
    yourWorkspace: 'votre espace de travail',
    figures: 'Indicateurs',
    errorGeneric: 'Une erreur s’est produite. Réessayez.',
    contacts: 'Contacts',
    newInPeriod_one: 'nouveau sur la période',
    newInPeriod_other: 'nouveaux sur la période',
    noneNew: 'aucun nouveau sur la période',
    importContacts: 'Importer des contacts →',
    messagesSent: 'Messages envoyés',
    nothingSent: 'rien envoyé pour l’instant',
    inPeriod: 'sur la période',
    activeAutomations: 'Automatisations actives',
    ofTotal: 'sur {{total}}',
    currentState: 'état actuel',
    draftsLeft_one: '{{count}} brouillon à terminer',
    draftsLeft_other: '{{count}} brouillons à terminer',
    startFromTemplate: 'Partir d’un modèle →',
    health: {
      title: 'Santé des automatisations',
      seeAll: 'Tout voir',
      fired_one: '{{count}} déclenchement',
      fired_other: '{{count}} déclenchements',
      succeeded: 'réussi',
      allSucceeded: 'tous réussis',
      failed_one: '{{count}} échec',
      failed_other: '{{count}} échecs',
      inProgress: '{{count}} en cours',
      none: 'Aucun déclenchement sur la période',
      silent: 'Active, aucun déclenchement depuis 30 jours',
      lastFailed: 'dernier {{when}}',
      legend: 'Vert = réussi · gris = rien à mesurer · rouge = échec à corriger',
      rate: 'Taux de réussite : {{value}}',
      noRate: 'Rien à mesurer',
      empty: 'Aucune automatisation pour l’instant. Avec un modèle, vous êtes en ligne en deux minutes environ.',
      browseTemplates: 'Voir les modèles',
    },
    token: {
      expires_one: 'Le jeton {{channel}} de {{account}} expire dans {{count}} jour',
      expires_other: 'Le jeton {{channel}} de {{account}} expire dans {{count}} jours',
      expired: 'Le jeton {{channel}} de {{account}} a expiré',
      renew: 'Renouveler',
    },
    conversations: {
      title: 'Conversations récentes',
      open: 'Ouvrir',
      emptyTitle: 'Aucune conversation pour l’instant',
      emptyBody: 'Les messages de vos canaux arrivent ici dès que quelqu’un vous écrit.',
      noMessages: 'Pas encore de message',
      link: 'Lien · {{domain}}',
      unread_one: '{{count}} message non lu',
      unread_other: '{{count}} messages non lus',
    },
    activity: {
      title: 'Activité récente',
      grouped: 'Regroupée par passage d’automatisation',
      empty: 'L’activité s’affiche ici dès que vos automatisations tournent.',
    },
    setup: {
      title: 'Mettre {{workspace}} en ligne',
      lede: 'Quatre étapes jusqu’à votre première conversation automatisée.',
      progress: 'Progression de la mise en route',
      done_one: '{{count}} étape sur {{total}} terminée',
      done_other: '{{count}} étapes sur {{total}} terminées',
      doneSr: '(terminée)',
      connect: 'Connecter un canal',
      connectBody: 'Instagram ou Messenger, en quelques clics',
      connected: '{{name}} connecté',
      build: 'Créer votre première automatisation',
      buildBody: 'Réponses sur les prix, messages de bienvenue, relances',
      import: 'Importer des contacts',
      importBody: 'Un CSV avec tags et champs personnalisés',
      invite: 'Inviter votre équipe',
      inviteBody: 'Les agents répondent aux conversations, les admins créent les automatisations',
    },
    /** "Prochaine mise en ligne" (Murmura M1, `P1-Dashboard.dc.html`). */
    next: {
      title: 'Prochaine mise en ligne',
      meta: '{{format}} · {{account}}',
      leavesIn: 'part dans',
      crossPosted: '{{platform}} + {{page}}',
      open: 'Ouvrir',
      then: 'Ensuite',
      thenMeta: '{{date}} · {{time}} · {{account}}',
      nothingElse: 'Rien d’autre de programmé.',
      untimed_one: 'La publication de {{name}} n’a pas encore d’horaire.',
      untimed_other: 'Les {{count}} publications de {{name}} n’ont pas encore d’horaire.',
    },
    /** "À traiter" (Murmura M1): what someone must act on, with the app's own actions. */
    todo: {
      title: 'À traiter',
      count_one: '{{count}} élément',
      count_other: '{{count}} éléments',
      invoice: {
        title: 'Facture {{number}} · {{amount}}',
        due: 'À régler avant le {{date}}.',
        overdue: 'En retard : à régler depuis le {{date}}.',
        view: 'Voir la facture',
      },
      failed: {
        post_one: '{{count}} publication en échec',
        post_other: '{{count}} publications en échec',
        story_one: '{{count}} story en échec',
        story_other: '{{count}} stories en échec',
        retry: 'Réessayer',
        view: 'Voir',
      },
      campaign: {
        untimed_one: '{{count}} publication sans horaire : la campagne ne peut pas être lancée.',
        untimed_other: '{{count}} publications sans horaire : la campagne ne peut pas être lancée.',
        setTimes: 'Définir les horaires',
      },
      automation: {
        notLive: 'Automatisation pas encore activée.',
        more_one: '+ {{count}} autre automatisation en brouillon',
        more_other: '+ {{count}} autres automatisations en brouillon',
        viewAll: 'Voir les automatisations',
      },
      clear: {
        both: 'Aucun échec de publication · aucune conversation non lue.',
        noFailures: 'Aucun échec de publication.',
        noUnread: 'Aucune conversation non lue.',
      },
      nothing: 'Rien à traiter.',
    },
  },
  /** The activity log in plain words (Tableau de bord, R01): one row per automation run, its steps as chips. */
  activity: {
    someAutomation: 'Une automatisation',
    someAutomationInline: 'une automatisation',
    aContact: 'un contact',
    aTag: 'un tag',
    aTeammate: 'Un membre de l’équipe',
    suspended: 'suspendu',
    reactivated: 'réactivé',
    line: {
      started: '{{automation}} a démarré pour {{contact}}',
      completed: '{{automation}} s’est terminée pour {{contact}}',
      failed: '{{automation}} a échoué pour {{contact}}',
      canceled: '{{automation}} a été arrêtée pour {{contact}}',
      limitBlocked: '{{automation}} a été bloquée par la limite mensuelle de l’offre',
      retriggerIgnored: '{{automation}} était déjà en cours pour {{contact}}',
      takeoverSuppressed: '{{automation}} est restée silencieuse pendant qu’un membre de l’équipe parlait à {{contact}}',
      workspaceSuspended: '{{automation}} s’est arrêtée : l’espace de travail a été suspendu',
      repliedBy: '{{automation}} a répondu à {{contact}}',
      sent: 'Un message a été envoyé à {{contact}}',
      reacted: '{{automation}} a réagi avec un cœur à {{contact}}',
      reactFailed: 'Une réaction (un cœur) à {{contact}} n’a pas pu être envoyée',
      commentReplied: '{{automation}} a répondu à un commentaire de {{contact}}',
      emailSent: 'Un e-mail a été envoyé à {{contact}}',
      tagged: '{{contact}} a reçu le tag {{tag}}',
      tagAdded: 'Tag {{tag}} ajouté',
      tagRemoved: 'Le tag {{tag}} a été retiré de {{contact}}',
      agentReplied: 'Un membre de l’équipe a répondu à {{contact}}',
      closed: 'La conversation avec {{contact}} a été clôturée',
      reopened: 'La conversation avec {{contact}} a été rouverte',
      paused: 'Automatisations mises en pause pour {{contact}}',
      resumed: 'Automatisations relancées pour {{contact}}',
      workspace: 'L’espace de travail a été {{state}}',
      contactErased: '{{member}} a effacé un contact',
    },
    /** One run on one row: "Maya Haddad — Lead Follow-up a répondu au commentaire, a envoyé un message puis s’est terminée". */
    run: '{{contact}} — {{automation}} {{steps}}',
    runNoContact: '{{automation}} {{steps}}',
    then: ' puis ',
    step: {
      started: 'a démarré',
      commentReplied: 'a répondu au commentaire',
      sent: 'a envoyé un message',
      reacted: 'a réagi avec un cœur',
      emailSent: 'a envoyé un e-mail',
      tagAdded: 'a ajouté le tag {{tag}}',
      tagRemoved: 'a retiré le tag {{tag}}',
      completed: 's’est terminée',
      failed: 'a échoué',
      canceled: 'a été arrêtée',
      limitBlocked: 'a été bloquée par la limite de l’offre',
    },
    chip: {
      commentReplied: 'Commentaire traité',
      sent: 'Message envoyé',
      reacted: 'Réaction envoyée',
      emailSent: 'E-mail envoyé',
      tagAdded: 'Tag ajouté',
      tagRemoved: 'Tag retiré',
      limitBlocked: 'Limite de l’offre',
    },
  },
  /** Statistiques (R29, `R29-Analytics.dc.html`). */
  analytics: {
    title: 'Statistiques',
    lede: 'Déclenchements, envois et conversions par automatisation.',
    figures: 'Indicateurs',
    errorGeneric: 'Une erreur s’est produite. Réessayez.',
    fired: 'Déclenchements',
    firedDetail: 'passages d’automatisation',
    successRate: 'Taux de réussite',
    successOf_one: '{{count}} réussi sur {{finished}}',
    successOf_other: '{{count}} réussis sur {{finished}}',
    noFinished: 'Aucun passage terminé',
    noRuns: 'Aucun déclenchement',
    failures_one: '{{count}} échec',
    failures_other: '{{count}} échecs',
    messagesSent: 'Messages envoyés',
    messagesDetail: 'par les automatisations',
    reached: 'Contacts atteints',
    reachedDetail: 'cumulés : une personne peut compter dans plusieurs automatisations',
    archived: 'Archivée',
    chart: {
      title: 'Déclenchements par jour',
      legend: 'Légende',
      succeeded: 'Réussis',
      failed: 'Échecs',
      loading: 'Chargement du graphique',
      partial: 'Additionne vos {{count}} automatisations les plus actives.',
      ok_one: '{{count}} réussi',
      ok_other: '{{count}} réussis',
      peakFailed_one: '{{ok}} + {{count}} échec',
      peakFailed_other: '{{ok}} + {{count}} échecs',
      noneOnDay: 'Aucun déclenchement',
      summary: '{{ok}} et {{failed}} sur {{days}}.',
      peak: 'Jour le plus actif : {{date}}, {{value}}.',
    },
    table: {
      title: 'Par automatisation',
      label: 'Statistiques par automatisation',
      noGoal: 'Aucune automatisation n’a d’objectif de conversion',
      automation: 'Automatisation',
      fired: 'Déclenchements',
      success: 'Réussite',
      messages: 'Messages',
      reached: 'Contacts atteints',
      conversions: 'Conversions',
      setGoal: 'Définir un objectif',
      converted: '{{rate}} convertis',
      footer: '« — » : pas encore active · « 0 » : rien envoyé sur la période',
    },
    empty: {
      title: 'Aucun déclenchement sur la période',
      body: 'Activez une automatisation : ses déclenchements, envois et conversions s’afficheront ici.',
      action: 'Voir les automatisations',
    },
  },
  /** Statistiques d’une automatisation (R30, `R30-Analytics-Automation.dc.html`). */
  automationAnalytics: {
    back: 'Statistiques',
    fallbackTitle: 'Automatisation',
    lede: 'Performances de cette automatisation sur la période choisie.',
    openEditor: 'Ouvrir dans l’éditeur',
    fired: 'Déclenchements',
    succeeded_one: '{{count}} réussi',
    succeeded_other: '{{count}} réussis',
    failed_one: '{{count}} échec',
    failed_other: '{{count}} échecs',
    messagesSent: 'Messages envoyés',
    reached: 'Contacts atteints',
    inPeriod: 'sur la période',
    conversions: 'Conversions',
    setGoal: 'Définir un objectif ↓',
    conversionRate: '{{rate}} des contacts atteints',
    chart: {
      title: 'Activité par jour',
      fired: 'Déclenchements',
      messages: 'Messages envoyés',
      noFired: 'Aucun déclenchement sur la période.',
      noMessages: 'Aucun message envoyé sur la période.',
      peak: '{{value}} · {{date}}',
      firedCount_one: '{{count}} déclenchement',
      firedCount_other: '{{count}} déclenchements',
      messagesCount_one: '{{count}} message envoyé',
      messagesCount_other: '{{count}} messages envoyés',
      summary: '{{fired}} et {{messages}} sur {{days}}.',
    },
    goal: {
      title: 'Objectif de conversion',
      configured: 'Configuré',
      notConfigured: 'Non configuré',
      body: 'Un déclenchement compte comme converti si le contact reçoit le tag choisi dans la fenêtre qui suit son début. Sans objectif, les conversions s’affichent « — » plutôt qu’un 0 trompeur.',
      tag: 'Tag objectif',
      chooseTag: 'Choisir un tag…',
      window: 'Fenêtre',
      windowHint: 'À partir du début du passage',
      w24: '1 jour (24 h)',
      w72: '3 jours (72 h)',
      w168: '1 semaine (168 h)',
      w720: '30 jours (720 h)',
      save: 'Enregistrer l’objectif',
      saving: 'Enregistrement…',
      remove: 'Retirer',
      saved: 'Objectif de conversion enregistré',
      removed: 'Objectif de conversion retiré',
      saveFailed: 'Impossible d’enregistrer l’objectif',
    },
  },
  /** Automatisations (R04): the list, its tabs, rows and menus. */
  automationsList: {
    title: 'Automatisations',
    lede: 'Des automatisations qui répondent aux messages, aux mots-clés et aux changements de contact.',
    templates: 'Modèles',
    new: 'Nouvelle automatisation',
    tabsLabel: 'Filtrer par statut',
    tabs: { all: 'Toutes', active: 'Actives', draft: 'Brouillons', paused: 'En pause' },
    search: 'Rechercher une automatisation',
    tableLabel: 'Automatisations',
    columns: {
      automation: 'Automatisation',
      status: 'Statut',
      fired: 'Déclenchements · 7 j',
      success: 'Réussite',
      last: 'Dernier déclenchement',
      actions: 'Actions',
    },
    origin: { quick: 'Rapide', custom: 'Personnalisée' },
    noTrigger: 'Pas encore de déclencheur',
    edited: { ago: 'Modifiée {{when}}', today: 'Modifiée à {{when}}', date: 'Modifiée le {{when}}' },
    finishSetup: 'Terminer la configuration',
    succeeded_one: 'réussi',
    succeeded_other: 'réussis',
    failed_one: '{{count}} échec',
    failed_other: '{{count}} échecs',
    fired_one: '{{count}} déclenchement · 7 j',
    fired_other: '{{count}} déclenchements · 7 j',
    lastRun: 'Dernier déclenchement',
    moreActions: 'Plus d’actions pour {{name}}',
    menu: {
      analytics: 'Statistiques',
      pause: 'Mettre en pause',
      resume: 'Réactiver',
      enable: 'Activer',
      rename: 'Renommer',
      duplicate: 'Dupliquer',
      delete: 'Supprimer',
    },
    swipe: { pause: 'Pause' },
    footer: '« — » : rien à mesurer · « 0 » : aucun déclenchement sur 7 jours',
    hint: 'Mettre en pause ou réactiver : menu ⋯ de chaque ligne.',
    statsError: 'Les déclenchements n’ont pas pu être chargés ; la liste, elle, est à jour.',
    loadError: 'Impossible de charger les automatisations.',
    errorGeneric: 'Une erreur est survenue.',
    validation: '« {{name}} » a des erreurs : ouvrez-la dans l’éditeur visuel pour les corriger avant de l’activer.',
    toast: {
      duplicated: 'Automatisation dupliquée',
      opening: 'Ouverture de « {{name}} ».',
      paused: '« {{name}} » est en pause',
      pausedBody: 'Elle ne réagit plus aux nouveaux déclencheurs.',
      live: '« {{name}} » est active',
      liveBody: 'La version publiée réagit désormais aux déclencheurs.',
      deleted: 'Automatisation supprimée',
      deletedBody: 'Son historique de déclenchements est conservé pour les statistiques.',
    },
    deleteDialog: {
      title: 'Supprimer « {{name}} » ?',
      body: 'L’automatisation cesse aussitôt de réagir. Son historique de déclenchements est conservé pour les statistiques.',
      cancel: 'Annuler',
      confirm: 'Supprimer l’automatisation',
    },
    renameDialog: {
      title: 'Renommer l’automatisation',
      name: 'Nom',
      cancel: 'Annuler',
      save: 'Enregistrer',
      failed: 'Le renommage a échoué.',
      nameNeeded: 'Saisissez un nom pour enregistrer.',
    },
    empty: {
      query: 'Aucune automatisation ne correspond à « {{query}} ».',
      all: 'Aucune automatisation pour l’instant.',
      active: 'Aucune automatisation active pour l’instant.',
      draft: 'Aucun brouillon.',
      paused: 'Aucune automatisation en pause.',
    },
    first: {
      title: 'Votre première automatisation est à deux minutes',
      body: 'Partez d’un modèle éprouvé et adaptez-le : chaque modèle crée un brouillon modifiable.',
    },
  },
  /** Modèles (R09), the gallery shared with the New automation modal (R43), and the 17 templates. */
  templates: {
    title: 'Modèles',
    lede: 'Des automatisations éprouvées à adapter : chaque modèle crée un brouillon modifiable.',
    goalsLabel: 'Objectif',
    all: 'Tous',
    goals: {
      grow: 'Gagner des abonnés',
      engage: 'Engager l’audience',
      traffic: 'Générer du trafic',
      leads: 'Collecter des leads',
      sell: 'Vendre plus',
    },
    search: 'Rechercher un modèle',
    trigger: 'Déclencheur',
    allTriggers: 'Tous les déclencheurs ({{count}})',
    triggerOption: '{{name}} ({{count}})',
    count_one: '{{count}} modèle',
    count_other: '{{count}} modèles',
    effort: { quick: 'Rapide · 2 min', custom: 'Éditeur visuel' },
    use: 'Utiliser',
    useLabel: 'Utiliser le modèle : {{name}}',
    listLabel: 'Modèles',
    moreSteps_one: '{{count}} étape de plus',
    moreSteps_other: '{{count}} étapes de plus',
    steps: {
      comment_received: 'Commentaire',
      live_comment_received: 'Live',
      story_reply_received: 'Story',
      story_mention_received: 'Mention',
      keyword_received: 'Mot-clé',
      message_received: 'Message',
      contact_created: 'Nouveau contact',
      tag_added: 'Tag ajouté',
      send_message: 'DM',
      reply_comment: 'Réponse publique',
      add_tag: 'Tag',
      wait: 'Attente',
      follows_account: 'Abonné ?',
      check: 'Vérification',
    },
    emptyTitle: 'Aucun modèle disponible',
    emptyBody: 'Les modèles intégrés sont fournis avec la plateforme ; s’il n’y en a aucun, le serveur n’est peut-être pas à jour.',
    noMatchTitle: 'Aucun modèle ne correspond',
    noMatchBody: 'Essayez un autre objectif ou déclencheur, ou effacez la recherche.',
    showAll: 'Afficher tous les modèles',
    created: '« {{name}} » créée',
    createdBody: 'Elle démarre en brouillon : relisez-la et activez-la quand elle est prête.',
    createFailed: 'Impossible de créer l’automatisation.',
    detail: {
      dialogTitle: 'Modèle',
      label: 'Détails de {{name}}',
      startsOn: 'Démarre sur : {{trigger}}',
      aTrigger: 'un déclencheur',
      value: 'Ce que ça vous apporte',
      how: 'Comment ça marche ?',
      createsTags_one: 'Crée ce tag s’il n’existe pas encore :',
      createsTags_other: 'Crée ces tags s’ils n’existent pas encore :',
      quickNote: 'Automatisation rapide : un formulaire guidé, sans canevas. Vous la mettez en ligne à la dernière étape.',
      customNote: 'Éditeur visuel : elle arrive en brouillon dans l’éditeur. Relisez les messages, puis activez-la.',
      back: 'Retour à la liste',
      use: 'Utiliser ce modèle',
      settingUp: 'Création…',
    },
    items: {
      quick_comment_to_dm: {
        name: 'Envoyer un lien en DM depuis les commentaires',
        description: 'Envoyez un lien quand on commente une publication ou un reel. Choisissez la publication, écrivez les messages, c’est en ligne, sans canevas.',
        value: 'Le moyen le plus rapide de transformer les commentaires en clics : prêt en une minute, sans toucher à l’éditeur visuel.',
        how: 'Choisissez une publication ou toutes, éventuellement les mots qu’un commentaire doit contenir, puis écrivez une réponse publique facultative et le DM avec le bouton de votre lien. Quand quelqu’un commente, il reçoit la réponse et le lien en DM.',
      },
      quick_story_link: {
        name: 'Générer des leads avec les stories',
        description: 'Envoyez un lien à toute personne qui répond à votre story : toutes les réponses, ou seulement celles avec vos mots-clés.',
        value: 'Les réponses aux stories viennent de votre audience la plus chaude : répondez à chacune aussitôt avec l’offre qui la fait réagir.',
        how: 'Choisissez si toute réponse compte ou seulement celles avec vos mots ou réactions, puis écrivez le DM et ajoutez le bouton de votre lien. Toute personne qui répond à votre story le reçoit aussitôt.',
      },
      quick_dm_link: {
        name: 'Répondre aux DM avec un lien',
        description: 'Envoyez automatiquement votre lien quand on vous écrit un mot-clé, ou répondez à chaque message.',
        value: 'Ne tapez plus jamais deux fois le même lien : ceux qui le demandent le reçoivent en quelques secondes, de jour comme de nuit.',
        how: 'Saisissez les mots qui la déclenchent (par exemple Prix ou Lien), ou choisissez n’importe quel message, puis écrivez la réponse et ajoutez le bouton de votre lien.',
      },
      welcome_new_lead: {
        name: 'Accueillir un nouveau lead',
        description: 'Salue chaque nouveau contact et lui ajoute le tag lead.',
        value: 'Chaque nouveau contact reçoit un accueil chaleureux en quelques secondes : la première impression ne dépend plus de votre présence en ligne.',
        how: 'Dès que quelqu’un devient un contact (premier message, commentaire ou import), il reçoit un message de bienvenue et le tag New Lead : vos nouveaux leads sont à un filtre près.',
      },
      keyword_pricing: {
        name: 'Tarifs par mot-clé',
        description: 'Répond avec vos tarifs quand on vous écrit « price ».',
        value: 'Arrêtez de taper vos tarifs vingt fois par jour. Ceux qui demandent ont la réponse aussitôt, tant qu’ils sont intéressés.',
        how: 'Quand un message contient « price », l’automatisation répond avec vos tarifs et un bouton pour en savoir plus, puis ajoute le tag Interested au contact : vous savez qui relancer.',
      },
      lead_followup: {
        name: 'Relance de lead',
        description: 'Attend un jour après le tag lead, puis relance, sauf si la personne a acheté.',
        value: 'Les leads qui se taisent reçoivent une relance douce automatiquement : aucun lead chaud ne vous échappe.',
        how: 'Quand un contact reçoit le tag New Lead, l’automatisation attend un jour. S’il n’a pas reçu le tag Purchased d’ici là, elle envoie une relance amicale ; s’il a acheté, elle s’arrête.',
      },
      comment_to_dm: {
        name: 'Commentaire → DM',
        description: 'Envoie un message privé à toute personne qui commente une publication choisie, et lui ajoute un tag.',
        value: 'Transformez les commentaires en conversations : chaque commentaire sur votre publication devient une discussion privée où vendre.',
        how: 'Choisissez une publication. Toute personne qui commente reçoit aussitôt un message privé de votre part et le tag Commented, pour pouvoir la recontacter plus tard.',
      },
      reply_to_comment: {
        name: 'Répondre aux commentaires',
        description: 'Répond publiquement sous chaque commentaire. Limitez à une publication dans le déclencheur, ou laissez ouvert.',
        value: 'Votre fil de commentaires paraît vivant et suivi, même quand vous êtes occupé à créer.',
        how: 'Chaque nouveau commentaire reçoit une réponse publique de votre compte, qui remercie la personne et l’invite à regarder ses DM.',
      },
      story_reply_dm: {
        name: 'Réponse à une story → DM',
        description: 'Envoie un DM à toute personne qui répond à une de vos stories, et lui ajoute un tag.',
        value: 'Les réponses aux stories sont vos leads les plus chauds. Répondez aussitôt et faites vivre la conversation.',
        how: 'Quand quelqu’un répond à une de vos stories, il reçoit aussitôt un message en retour et le tag Story reply.',
      },
      follower_only_link: {
        name: 'Lien réservé aux abonnés',
        description: 'Quand on envoie LINK, vérifie l’abonnement : les abonnés reçoivent le lien, les autres sont invités à s’abonner.',
        value: 'Récompensez vos abonnés et transformez chaque demande de lien en nouvel abonnement.',
        how: 'Quand quelqu’un envoie LINK, l’automatisation vérifie s’il vous suit. Les abonnés reçoivent le lien ; les autres sont invités à s’abonner, puis à renvoyer LINK.',
      },
      default_reply: {
        name: 'Répondre à tous vos DM',
        description: 'Répond immédiatement à chaque message privé : personne n’attend en silence pendant votre absence.',
        value: 'Personne n’attend en silence. Chaque message reçoit une réponse immédiate qui dit à quoi s’attendre.',
        how: 'Chaque message reçu obtient une réponse automatique annonçant que vous répondrez bientôt. Une réponse depuis Conversations met les automatisations en pause pour ce contact : elle ne coupe jamais la parole à votre équipe.',
      },
      giveaway: {
        name: 'Organiser un jeu-concours',
        description: 'Chaque commentaire reçoit une réponse publique « regardez vos DM » et un DM pour participer. Les abonnés sont inscrits et tagués.',
        value: 'Un jeu-concours attire une foule de commentaires et de nouveaux abonnés. Celui-ci gère chaque participation pour vous, équitablement et aussitôt.',
        how: 'Chaque personne qui commente reçoit une réponse publique « Regardez vos DM » et un DM avec un bouton pour participer. Quand elle appuie, l’automatisation vérifie si elle vous suit : les abonnés sont inscrits et reçoivent le tag Giveaway entrant, les autres sont invités à s’abonner puis à appuyer sur « Je suis abonné ».',
      },
      comment_follow_gate: {
        name: 'Gagner des abonnés grâce aux commentaires',
        description: 'On commente pour le lien et on touche le bouton du DM : les abonnés reçoivent le lien, les autres sont invités à s’abonner.',
        value: 'Chaque commentaire qui demande votre lien devient une occasion de gagner un abonné.',
        how: 'Quelqu’un commente votre publication et reçoit un DM avec un bouton « Envoyez-moi le lien ». Quand il appuie, les abonnés reçoivent le lien ; les autres sont invités à s’abonner puis à appuyer sur « Je suis abonné ».',
      },
      story_coupon: {
        name: 'Offrir des coupons en story',
        description: 'Répondre à votre story fait gagner un code promo secret en DM, et le contact est tagué pour les relances.',
        value: 'Faites de vos spectateurs de stories des VIP et transformez leur attention en ventes.',
        how: 'Quand quelqu’un répond à votre story, il reçoit un code promo secret en DM et le tag Coupon sent : vous voyez qui a reçu l’offre.',
      },
      story_mention_thanks: {
        name: 'Remercier ceux qui vous mentionnent',
        description: 'Quand on vous mentionne en story, la personne reçoit un DM de remerciement avec un petit cadeau et un tag.',
        value: 'Ceux qui vous mentionnent sont vos meilleurs ambassadeurs. Remerciez-les et donnez-leur une raison de recommencer.',
        how: 'Quand quelqu’un mentionne votre compte dans sa story, il reçoit un message de remerciement avec une petite récompense et le tag Mentioned us.',
      },
      live_comment_link: {
        name: 'Envoyer un lien pendant votre live',
        description: 'Les spectateurs qui commentent LINK pendant un live reçoivent le lien en DM, sans que vous quittiez le direct.',
        value: 'Captez les acheteurs sur le moment, sans interrompre votre live pour coller des liens.',
        how: 'Pendant un live, toute personne qui commente LINK reçoit le lien en message privé. Les autres commentaires du live ne sont pas concernés.',
      },
      collab_requests: {
        name: 'Répondre aux demandes de collab',
        description: 'Les messages qui parlent de collab reçoivent votre media kit automatiquement, avec un tag pour le suivi.',
        value: 'Passez moins de temps à copier-coller votre media kit et plus de temps à conclure des collaborations.',
        how: 'Quand un message parle de collab, de partenariat ou de sponsor, l’automatisation répond avec votre media kit et ajoute le tag Collab request au contact pour le suivi.',
      },
    },
  },
  /** Automatisation rapide (R05): the steps, their fields, the preview and what can block them. */
  contacts: {
    title: 'Contacts',
    lede_one: '{{count}} personne dans cet espace',
    lede_other: '{{count}} personnes dans cet espace',
    ledeEmpty: 'Personne pour l’instant',
    export: 'Exporter',
    exporting: 'Export…',
    exportFailed: 'Impossible d’exporter les contacts',
    importCsv: 'Importer un CSV',
    new: 'Nouveau contact',
    segmentsLabel: 'Segments',
    allContacts: 'Tous les contacts',
    newSegment: 'Nouveau segment',
    segmentActions: 'Actions du segment {{name}}',
    editSegment: 'Modifier le segment',
    deleteSegment: 'Supprimer le segment',
    deleteSegmentFailed: 'Impossible de supprimer le segment',
    searchLabel: 'Rechercher des contacts',
    search: 'Nom, identifiant, e-mail ou téléphone',
    filters: {
      all: 'tous',
      tag: 'Tag : {{value}}',
      channel: 'Canal : {{value}}',
      status: 'Statut : {{value}}',
      tagLabel: 'Filtrer par tag',
      channelLabel: 'Filtrer par canal',
      statusLabel: 'Filtrer par statut',
      field: 'Champ',
      fieldValue: '{{field}} {{op}} {{value}}',
      fieldList: 'Champ',
      condition: 'Condition',
      value: 'Valeur',
      valuePlaceholder: 'ex. Paris',
      apply: 'Appliquer',
      ops: { eq: 'est', contains: 'contient', gt: 'supérieur à', lt: 'inférieur à' },
    },
    statuses: { active: 'Actif', unsubscribed: 'Désabonné', blocked: 'Bloqué' },
    mockChannel: 'Mock (dev)',
    columns: { contact: 'Contact', tags: 'Tags', channel: 'Canal', last: 'Dernière activité', added: 'Ajouté le', email: 'E-mail' },
    selectAll: 'Tout sélectionner sur cette page',
    select: 'Sélectionner {{name}}',
    listLabel: 'Contacts',
    noTags: 'Aucun tag',
    noEmail: 'Pas d’e-mail',
    pager: '{{from}}–{{to}} sur {{total}}',
    previousPage: 'Page précédente',
    nextPage: 'Page suivante',
    unnamed: 'Contact sans nom',
    errorGeneric: 'Une erreur est survenue. Réessayez.',
    empty: {
      title: 'Faites venir votre audience',
      body: 'Importez un CSV avec des tags et des champs personnalisés. Les nouvelles personnes apparaissent aussi ici automatiquement la première fois qu’elles vous écrivent ou commentent.',
      import: 'Importer un CSV',
      manual: 'Ajouter manuellement',
      sample: 'Télécharger un exemple de CSV',
      noMatchTitle: 'Aucun contact ne correspond à ces filtres',
      noMatchBody: 'Élargissez la recherche ou retirez un filtre.',
      clear: 'Effacer les filtres',
      segmentTitle: 'Personne dans ce segment pour l’instant',
      segmentBody: 'Les contacts apparaissent ici dès qu’ils remplissent ses conditions.',
    },
    bulk: {
      label: 'Actions groupées',
      selected_one: '{{count}} sélectionné',
      selected_other: '{{count}} sélectionnés',
      addTag: 'Ajouter un tag',
      removeTag: 'Retirer un tag',
      delete: 'Supprimer',
      clear: 'Désélectionner',
      noTags: 'Aucun tag pour l’instant.',
      tagAdded: 'Tag ajouté',
      tagRemoved: 'Tag retiré',
      addedBody_one: '{{count}} contact tagué.',
      addedBody_other: '{{count}} contacts tagués.',
      removedBody_one: 'Retiré de {{count}} contact.',
      removedBody_other: 'Retiré de {{count}} contacts.',
      tagFailed: 'Impossible de modifier les tags',
      deleted_one: '{{count}} contact supprimé',
      deleted_other: '{{count}} contacts supprimés',
      notDeleted_one: '{{count}} contact n’a pas été supprimé',
      notDeleted_other: '{{count}} contacts n’ont pas été supprimés',
      confirmTitle_one: 'Supprimer {{count}} contact ?',
      confirmTitle_other: 'Supprimer {{count}} contacts ?',
      confirmBody: 'Ils sont retirés de votre liste de contacts. Cette action est définitive.',
      cancel: 'Annuler',
      confirm_one: 'Supprimer {{count}} contact',
      confirm_other: 'Supprimer {{count}} contacts',
      deleting: 'Suppression…',
    },
    form: {
      firstName: 'Prénom',
      lastName: 'Nom',
      email: 'E-mail',
      phone: 'Téléphone',
      username: 'Identifiant',
      status: 'Statut',
      notes: 'Notes',
      notesNote: 'visibles par l’équipe',
      emailPlaceholder: 'ex. nom@domaine.com',
      phonePlaceholder: 'ex. +216 20 000 000',
      usernamePlaceholder: 'ex. @nom_utilisateur',
      notesPlaceholder: 'ex. A demandé les tarifs de gros',
      notSet: 'Non renseigné',
      yes: 'Oui',
      no: 'Non',
    },
    create: {
      title: 'Nouveau contact',
      cancel: 'Annuler',
      submit: 'Créer le contact',
      creating: 'Création…',
      needsOne: 'Renseignez au moins un nom, un e-mail, un téléphone ou un identifiant.',
    },
    import: {
      title: 'Importer des contacts depuis un CSV',
      help: 'La première ligne doit contenir les en-têtes de colonnes. Colonnes reconnues : first_name, last_name, username, email, phone, notes, tags (séparés par ;) et toute clé de champ personnalisé. Les colonnes inconnues sont ignorées, tout comme les lignes dont l’e-mail existe déjà.',
      choose: 'Choisir un fichier .csv',
      pickFirst: 'Choisissez d’abord un fichier.',
      rows: 'Lignes',
      imported: 'Importées',
      skipped: 'Ignorées',
      row: 'Ligne',
      problem: 'Problème',
      clean: 'Toutes les lignes ont été importées.',
      done: 'Terminé',
      cancel: 'Annuler',
      submit: 'Importer',
      importing: 'Import…',
    },
    detail: {
      loading: 'Chargement du contact',
      notFound: 'Contact introuvable',
      back: 'Contacts',
      added: 'ajouté le {{date}}',
      last: {
        ago: 'dernière interaction {{when}}',
        today: 'dernière interaction à {{when}}',
        date: 'dernière interaction le {{when}}',
      },
      noInteraction: 'aucune interaction pour l’instant',
      save: 'Enregistrer',
      saving: 'Enregistrement…',
      noChanges: 'Aucune modification',
      unsaved: 'Modifications non enregistrées',
      saved: 'Enregistré',
      savedToast: 'Contact enregistré',
      profile: 'Profil',
      customFields: 'Champs personnalisés',
      manageFields: 'Gérer les champs',
      loadingFields: 'Chargement des champs',
      noFields: 'Aucun champ personnalisé pour l’instant. Un administrateur peut en ajouter dans',
      noFieldsLink: 'Paramètres › Champs personnalisés',
      dangerTitle: 'Supprimer ce contact',
      dangerBody: 'Retire {{name}} de l’espace {{workspace}}.',
      dangerBodyPlain: 'Retire {{name}} de cet espace.',
      adminsOnly: 'Seuls les administrateurs peuvent supprimer un contact.',
      delete: 'Supprimer le contact',
      confirmTitle: 'Supprimer {{name}} ?',
      confirmBody: 'Le contact est retiré de votre liste. Cette action est définitive.',
      cancel: 'Annuler',
      deleting: 'Suppression…',
      deletedToast: 'Contact supprimé',
      tags: 'Tags',
      manageTags: 'Gérer les tags',
      noTags: 'Aucun tag pour l’instant.',
      addTag: 'Ajouter un tag…',
      addTagLabel: 'Ajouter un tag',
    },
  },
  filterPill: {
    filterBy: 'Filtrer : {{label}}',
    is: '{{label}} : {{value}}',
    remove: 'Retirer le filtre {{label}}',
    panel: 'Filtre {{label}}',
  },
  quick: {
    breadcrumb: 'Fil d’Ariane',
    kinds: {
      comment: { title: 'Envoyer un lien en DM à chaque commentaire', lede: 'Répondez en privé à toute personne qui commente une publication.' },
      dm: { title: 'Répondre à vos DM', lede: 'Répondez automatiquement aux messages avec un lien.' },
      story: { title: 'Générer des leads avec les stories', lede: 'Envoyez un lien à toute personne qui répond à votre story.' },
    },
    cancel: 'Annuler',
    stepsLabel: 'Étapes',
    liveAfter: 'Mise en ligne après l’étape {{count}}',
    liveEdit: 'Les modifications s’appliquent dès la publication',
    recap: {
      title: 'Ce qui sera mis en ligne',
      titleEdit: 'Ce qui sera publié',
      account: 'Compte',
      allChannels: 'Tous les canaux connectés',
      trigger: 'Déclencheur',
      commentsOn: 'Commentaires sur « {{caption}} »',
      commentsOnScheduled: 'Commentaires sur « {{caption}} », programmée',
      commentsAny: 'Commentaires sur toutes les publications',
      chosenPost: 'la publication choisie',
      messages: 'Messages privés reçus',
      storyOne: 'Réponses à la story « {{label}} »',
      storyAny: 'Réponses à toutes vos stories',
      words: 'Mots',
      anyWord: 'N’importe lesquels',
      quoted: '« {{word}} »',
      sends: 'Envoi',
      dmWithLink: 'Un DM avec le bouton « {{label}} » vers {{host}}',
      dmOnly: 'Un DM',
      publicReply: 'une réponse publique sous le commentaire',
      heart: 'un cœur sur leur message',
      followersOnly: 'le lien aux abonnés seulement',
      consequence: {
        comment: 'Dès la mise en ligne, elle répond aux vrais commentaires de vos clients.',
        dm: 'Dès la mise en ligne, elle répond aux vrais messages de vos clients.',
        story: 'Dès la mise en ligne, elle répond aux vraies réponses à vos stories.',
        scheduled: 'Elle s’active à la mise en ligne de la publication, puis répond aux vrais commentaires.',
        liveEdit: 'Cette automatisation est active : les modifications s’appliquent aux vrais clients dès la publication.',
      },
    },
    stepOf: 'Étape {{n}} sur {{total}}',
    stepOfNamed: 'Étape {{n}} sur {{total}} · {{name}}',
    backTo: 'Revenir à l’étape {{n}} : {{name}}',
    stepDone: 'terminée',
    step: {
      post: 'Publication',
      comment: 'Commentaires',
      message: 'Message reçu',
      story: 'Story',
      reply: 'Réponses',
      dm: 'Message privé',
      dmReply: 'Votre réponse',
    },
    nextTo: {
      comment: 'Suivant : commentaires →',
      dm: 'Suivant : message privé →',
      dmReply: 'Suivant : votre réponse →',
      reply: 'Suivant : réponses →',
    },
    previous: '← Précédent',
    goLive: 'Mettre en ligne',
    publishing: 'Mise en ligne…',
    publishChanges: 'Publier les modifications',
    openBuilder: 'Ouvrir dans l’éditeur visuel',
    loading: 'Chargement de l’automatisation…',
    title: {
      post: 'Quand quelqu’un commente…',
      comment: 'Et ce commentaire contient…',
      message: 'Quand quelqu’un vous écrit…',
      story: 'Quand quelqu’un répond à…',
      reply: 'Et cette réponse contient…',
      dmComment: 'Ils recevront…',
      dmDm: 'Ils recevront votre réponse en DM',
      dmStory: 'Ils recevront le DM',
    },
    account: 'Compte',
    whichPosts: 'Sur quelles publications ?',
    whichComments: 'Quels commentaires ?',
    whichMessages: 'Quels messages ?',
    whichStories: 'Sur quelles stories ?',
    whichReplies: 'Quelles réponses ?',
    specificPost: 'Une publication ou un reel précis',
    anyPost: 'N’importe quelle publication ou reel',
    anyPostHint: 'Toutes les publications du compte, y compris celles publiées plus tard.',
    postSource: 'Type de publication',
    published: 'Publiées',
    scheduled: 'Programmées',
    showAll: 'Tout afficher',
    scheduledHint: 'Une publication programmée active l’automatisation dès sa mise en ligne.',
    untitledPost: 'Publication sans légende',
    loadingPosts: 'Chargement des publications…',
    noPosts: 'Ce compte n’a pas encore de publication à surveiller.',
    postIdLabel: 'Identifiant de la publication',
    postIdHint: 'Ce canal ne peut pas lister ses publications : collez l’identifiant tiré de l’URL de la publication.',
    idPlaceholder: 'ex. 17900000000000000',
    noImage: 'Pas d’image',
    scheduledGrid: {
      loading: 'Chargement des publications programmées…',
      error: 'Vos publications programmées n’ont pas pu être chargées.',
      retry: 'Réessayer',
      none: 'Rien de programmé sur ce compte pour l’instant.',
      schedule: 'Programmer une publication',
      goesLive: 'L’automatisation s’active à la mise en ligne de cette publication.',
    },
    anyStory: 'N’importe quelle story',
    anyStoryHint: 'Toutes les stories du compte, y compris celles publiées plus tard.',
    specificStory: 'Une story précise',
    specificStoryHint: 'Seules les stories en ligne en ce moment sont listées. Une story dure 24 heures ; une fois disparue, plus personne ne peut y répondre et l’automatisation se tait.',
    loadingStories: 'Chargement des stories…',
    noStories: 'Aucune story en ligne sur ce compte pour l’instant. Publiez-en une, puis choisissez-la ici.',
    storyIdLabel: 'Identifiant de la story',
    storyIdHint: 'Ce canal ne peut pas lister ses stories : collez l’identifiant de la story.',
    match: {
      specificWords: 'Un ou plusieurs mots précis',
      specificReply: 'Des mots ou réactions précis',
      anyWord: 'N’importe quel mot',
      anyMessage: 'N’importe quel message',
      anyMessageHint: 'Chaque DM la déclenche, comme une réponse par défaut.',
      anyReply: 'N’importe quel mot ou réaction',
    },
    keywords: {
      label: 'Mots à rechercher',
      placeholder: 'ex. prix, lien',
      hint: 'Séparez les mots par des virgules',
      examples: 'Par exemple :',
      example1: 'Prix',
      example2: 'Lien',
      example3: 'Boutique',
    },
    publicReply: {
      label: 'Répondre à leur commentaire sous la publication',
      hint: 'Publiée sous le commentaire, visible par tout le monde.',
      field: 'Réponse publique',
    },
    dm: {
      title: 'Un DM avec un lien',
      field: 'Message du DM',
      placeholder: 'ex. Merci pour votre commentaire, voici le lien',
      left_one: '{{count}} caractère restant',
      left_other: '{{count}} caractères restants',
      buttonLabel: 'Libellé du bouton',
      buttonLabelPlaceholder: 'ex. Ouvrir le guide',
      link: 'Lien',
      linkPlaceholder: 'ex. https://exemple.com',
      removeLink: 'Retirer le lien',
      addLink: '+ Ajouter un lien',
      oneMessage: 'Tout passe dans ce seul message : {{platform}} ne nous laisse écrire qu’une fois à une personne qui a commenté, jusqu’à ce qu’elle réponde.',
      platform: 'la plateforme',
    },
    other: 'Autres choses à automatiser',
    reactStory: 'Réagir aux réponses à votre story avec un cœur',
    reactDm: 'Réagir à leur message avec un cœur',
    reactHint: 'Un cœur apparaît sur leur message, avant votre réponse.',
    followGate: 'Demander de s’abonner avant d’envoyer le lien',
    followGateHint: 'Les abonnés reçoivent le lien. Les autres reçoivent ce message, et une fois abonnés, ils écrivent à nouveau.',
    askField: 'Message pour les personnes qui ne vous suivent pas',
    channelError: 'Vos canaux n’ont pas pu être chargés.',
    retry: 'Réessayer',
    noChannel: 'Aucun canal n’est encore connecté : cette automatisation répondrait sur tous les canaux.',
    connect: 'En connecter un',
    channelsFailed: 'Vos canaux n’ont pas pu être chargés : l’automatisation ne peut pas être limitée à un compte. Réessayez ci-dessus, puis mettez-la en ligne.',
    createFailed: 'Impossible de créer l’automatisation.',
    description: 'Créée avec l’automatisation rapide « {{title}} ».',
    toast: {
      live: 'Votre automatisation est active',
      liveBody: 'Elle répond dès maintenant. Vous la retrouvez dans vos automatisations.',
      set: 'Votre automatisation est prête',
      setBody: 'Elle s’active à la mise en ligne de la publication. Vous la retrouvez dans vos automatisations.',
      draft: 'Enregistrée en brouillon',
      draftBody_one: 'Il reste {{count}} problème à corriger dans l’éditeur visuel avant de publier.',
      draftBody_other: 'Il reste {{count}} problèmes à corriger dans l’éditeur visuel avant de publier.',
    },
    leave: {
      title: 'Abandonner cette automatisation ?',
      titleChanges: 'Abandonner les modifications ?',
      changesLost: 'L’automatisation reste telle qu’elle est ; ce que vous avez modifié ici sera perdu.',
      draftKept: 'Le brouillon de la tentative précédente reste dans vos automatisations ; ce que vous avez modifié depuis sera perdu.',
      nothingSaved: 'Rien n’est encore enregistré. Ce que vous avez configuré ici sera perdu.',
      keep: 'Continuer la modification',
      discard: 'Abandonner',
    },
    preview: {
      title: 'Aperçu',
      tabsLabel: 'Vue',
      tabs: { post: 'Publication', comments: 'Commentaires', story: 'Story', dm: 'DM' },
      yourAccount: 'votre compte',
      selectedPost: 'Publication choisie',
      imageFailed: 'Cette image n’a pas pu être chargée',
      noThumbnail: 'Cette publication n’a pas de miniature',
      pickPost: 'Choisissez une publication pour la voir ici',
      noCaption: 'Sans légende',
      viewComments: 'Voir les commentaires',
      everyPost: 'Toutes les publications du compte sont surveillées.',
      scheduledPost: 'Publication programmée : l’automatisation s’active à sa mise en ligne.',
      comments: 'Commentaires',
      sampleComment: 'J’adore !',
      follower: 'un_abonne',
      now: 'maintenant',
      noPublicReply: 'Pas de réponse publique : le contact reçoit seulement le DM.',
      addComment: 'Ajouter un commentaire…',
      reacted: 'Réaction avec un cœur',
      selectedStory: 'Story choisie',
      storyFailed: 'Cette story n’a pas pu être chargée',
      storyNoPreview: 'Cette story n’a pas d’aperçu',
      pickStory: 'Choisissez une story pour la voir ici',
      anyStory: 'Votre automatisation fonctionnera pour toutes les stories',
      sendMessage: 'Envoyer un message…',
      hi: 'Bonjour !',
      repliedToStory: 'A répondu à votre story',
      story: 'Story',
      writeDm: 'Rédigez le DM pour le voir ici.',
      gateNote: 'Ci-dessus : ce que reçoivent les abonnés. Pas encore abonné ? Il reçoit :',
      message: 'Message…',
    },
    problems: {
      words: {
        comment: 'Ajoutez au moins un mot à rechercher dans le commentaire.',
        message: 'Ajoutez au moins un mot à rechercher dans le message.',
        reply: 'Ajoutez au moins un mot à rechercher dans la réponse.',
      },
      tooManyWords: 'Gardez {{max}} mots au maximum.',
      wordTooLong: 'Gardez chaque mot sous {{max}} caractères.',
      publicReplyEmpty: 'Rédigez la réponse publique, ou désactivez-la.',
      publicReplyLong: 'Gardez la réponse publique sous {{max}} caractères.',
      askEmpty: 'Rédigez le message pour les personnes qui ne vous suivent pas encore.',
      askEmptyOrOff: 'Rédigez le message pour les personnes qui ne vous suivent pas encore, ou désactivez la vérification d’abonnement.',
      askLong: 'Gardez le message pour les non-abonnés sous {{max}} caractères.',
      post: 'Choisissez une publication pour continuer.',
      story: 'Choisissez une story pour continuer.',
      dmEmpty: 'Rédigez le DM qu’ils recevront.',
      dmLong: 'Gardez le DM sous {{max}} caractères.',
      buttonLabel: 'Donnez un libellé au bouton du lien.',
      buttonLong: 'Gardez le libellé du bouton sous {{max}} caractères.',
      buttonUrl: 'Saisissez un lien complet en https:// pour le bouton.',
      linkMissing: 'Ajoutez le lien que ce DM envoie.',
      storyNeedsInstagram: 'Les réponses aux stories n’existent que sur Instagram : connectez un compte Instagram.',
    },
  },
} as const;

type Widen<T> = { readonly [K in keyof T]: T[K] extends string ? string : Widen<T[K]> };

/** The shape every locale's catalogue has: French's keys, any string values. */
export type Messages = Widen<typeof fr>;
